import {
	createOffer,
	createPDFOfferJob,
	createPDFDimensioning,
	fetchOfferWithItemsById,
	fetchPriceListProducts as fetchPriceListProductsById,
	fetchPriceLists,
	showAlertModal,
	updateOffer,
	updateSelectedProducts,
	isObject,
	getClientInfo,
	fetchLocationById,
} from "@/utils/utils";
import { createLocationFromImportedOffer } from "@/utils/importOfferLocation";
import emitter from "@/utils/emitter";
import apiClient from "@/utils/apiClient";
import { cloneDeep } from "lodash";
function isCompositeOfferId(offerId) {
	return (
		typeof offerId === "string" &&
		(/^C\d+$/.test(offerId) || /^CO\d+$/.test(offerId))
	);
}

async function probeCompositeOffer(id) {
	// Only probe when the id looks like a composite offer id (C… or CO…).
	const isCompositeId =
		typeof id === "string" && (/^C\d+$/.test(id) || /^CO\d+$/.test(id));

	if (!isCompositeId) {
		// Normal offers (plain numeric ids etc.) should not hit the composite endpoint at all.
		return null;
	}

	const realId = id.match(/^C\d+$/) ? id.slice(1) : id.slice(2);

	try {
		const resp = await apiClient.get(`/api/composite-offers/${realId}/`, {
			validateStatus: status => status === 200 || status === 404,
		});
		if (resp.status === 404) {
			return null;
		}
		return resp.data;
	} catch (err) {
		// Normal (non‑composite) offers may lead to 404 or cancelled requests here;
		// those are expected and should not spam the console.
		if (err && err.response && err.response.status === 404) {
			return null;
		}
		if (err && err.code === "ERR_CANCELED") {
			return null;
		}
		console.error("Unexpected error while probing composite:", err);
		return null;
	}
}

export default {
	data() {
		return {
			initialOfferProducts: [],
			// Alert-only: snapshot of saved offer lines for price-list mismatch warnings; not used for save or calculation.
			savedOfferItemsSnapshot: [],
			isProductsChanged: false,
			discount: 0,
			clientId: null,
			locationId: null,
			offer: null,
			offerProducts: null,
			offerPaymentTerms: [],
			offeredProducts: null,
			offerInformation: {
				price_list_id: "",
				discount: 0,
				notes: "",
				locationVisibleOnPdf: false,
				areaVisibleOnPdf: false,
				totalFinalPrice: 0,
				totalPriceWithoutDiscount: 0,
			},
			client: null,
			location: null,
			initialLocation: null,
			offerId: null,
			offerPriceList: null,
			_pendingImportedDiscount: null,
			_importedDiscountSyncAttempts: 0,
			/** Prevents duplicate createOffer/saveOffer when VMC savedLocation watcher fires during an in-flight write */
			_offerWriteInFlight: false,
			_priceListSyncPromise: null,
			isOfferButtonsInView: true,
			offerTypes: [
				{
					name: "IPA",
					type: 1,
					routeName: "",
					color: "#e32829",
				},
				{
					name: "Sanitare",
					type: 2,
					routeName: "OfferPlumbing",
					color: "#8774B3",
				},
				{
					name: "VMC",
					type: 3,
					routeName: "OfferVMC",
					color: "#57c0da",
				},
				{
					name: "Simple",
					type: 4,
					routeName: "OfferSimple",
					color: "black",
				},
				{
					name: "Technical Room",
					type: 5,
					routeName: "OfferTechnicalRoom",
					color: "#8D9B6C",
				},
				{
					name: "Ceiling",
					type: 6,
					routeName: "OfferCeiling",
					color: "#3777BB",
				},
				{
					name: "Walls",
					type: 7,
					routeName: "OfferWalls",
					color: "#F8AC8C",
				},
				{
					name: "Automation",
					type: 8,
					routeName: "OfferAutomation",
					color: "#1F4690",
				},
				{
					name: "FanCoilUnit",
					type: 9,
					routeName: "OfferFanCoilUnit",
					color: "#AB9E9A",
				},
				{
					name: "ThermalChannel",
					type: 10,
					routeName: "OfferThermalChannel",
					color: "#b269ab",
				},
				{
					name: "HeatPump",
					type: 11,
					routeName: "OfferPDC",
					color: "#F37928",
				},
				{
					name: "Vacuum",
					type: 12,
					routeName: "OfferVacuum",
					color: "#40BFB4",
				},
				{
					name: "Services",
					type: 13,
					routeName: "OfferServices",
					color: "blue",
				},
				{
					name: "IPA_NEW",
					type: 20,
					routeName: "OfferIPA",
					color: "#e32829",
				},
			],
			buttons: [
				{
					text: "Calculează produse",
					onClick: async () => {
						await this.awaitPendingPriceListSync();
						return this.processRequiredProducts();
					},
					disabled: () => !this.offerId && !!this.$route?.query?.importOfferId,
					color: "success",
					visible: () =>
						this.$options.name === "OfferVMC" ||
						this.$options.name === "OfferPDC" ||
						this.$options.name === "OfferVacuum" ||
						this.$options.name === "OfferPlumbing" ||
						this.$options.name === "OfferCeiling" ||
						this.$options.name === "OfferIPA",
					dataCy: "calculeaza-produse",
				},
				{
					text: "Salvează oferta",
					onClick: this.createSaveOffer,
					disabled: () => !this.canSaveOffer,

					color: "success",
					visible: () => true,
				},
				{
					text: "Creează ofertă nouă",
					onClick: this.createNewOffer,
					disabled: () =>
						!this.offerId ||
						(this.$options.name === "OfferIPA" && this.needRecalc),
					color: "primary",
					visible: () => true,
				},
			],
			pdfOptions: {
				simple: [
					{
						text: "Ofertă cu preț",
						onClick: () =>
							createPDFOfferJob(
								this.offerId,
								0,
								this.sortingPDF,
								this
							),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă fără preț",
						onClick: () =>
							createPDFOfferJob(
								this.offerId,
								1,
								this.sortingPDF,
								this
							),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă fără preț și cantitate",
						onClick: () =>
							createPDFOfferJob(
								this.offerId,
								3,
								this.sortingPDF,
								this
							),
						disabled: () => !this.offerId,
						color: "primary",
					},
				],
				vmc: [
					{
						text: "Ofertă cu preț",
						onClick: () =>
							createPDFOfferJob(this.offerId, 0, 0, this),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă fără preț",
						onClick: () =>
							createPDFOfferJob(this.offerId, 1, 0, this),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă fără preț și cantitate",
						onClick: () =>
							createPDFOfferJob(this.offerId, 3, 0, this),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă generală",
						onClick: () =>
							createPDFOfferJob(this.offerId, 2, 0, this),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Dimensionare",
						onClick: () =>
							createPDFDimensioning(
								this.offerId,
								this.locationId,
								0
							),
						disabled: () => !this.offerId,
						color: "primary",
					},
				],
				ipa: [
					{
						text: "Ofertă cu preț",
						onClick: () => this.openOfferPDFMenu(0),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă fără preț",
						onClick: () => this.openOfferPDFMenu(1),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă fără preț și cantitate",
						onClick: () => this.openOfferPDFMenu(3),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Ofertă generală",
						onClick: () => this.openOfferPDFMenu(2),
						disabled: () => !this.offerId,
						color: "primary",
					},
					{
						text: "Dimensionare",
						onClick: () =>
							createPDFDimensioning(
								this.offerId,
								this.locationId,
								0
							),
						disabled: () => !this.offerId,
						color: "primary",
					},
				],
			},
			measurementUnits: [
				{ value: 1, text: "m²" },
				{ value: 2, text: "m" },
				{ value: 3, text: "pungă" },
				{ value: 4, text: "buc" },
				{ value: 5, text: "l" },
				{ value: 6, text: "set" },
				{ value: 7, text: "cutie" },
				{ value: 8, text: "rolă" },
				{ value: 9, text: "km" },
				{ value: 10, text: "kg" },
				{ value: 11, text: "ora" },
			],
			loadProductTable: false,
			savedOfferPriceListId: null,
			isChangedOfferPriceList: false,
			isDifferentCreator: true,
			isTechnicalCreator: false,
			onlyDiscountChanged: false,
			isCustomPDFHeaderEmpty: false,
		};
	},
	async created() {
		let offerAndProducts = {};

		if (!this.$route.params.offerId) {
			this.offerId = null;
		} else {
			this.offerId = this.$route.params.offerId;

			const compositeData = await probeCompositeOffer(this.offerId);

			if (compositeData) {
				this._hydrateComposite(compositeData);
				await this.captureCompositeSavedOfferItemsSnapshot(compositeData);

				offerAndProducts = compositeData;
			} else {
				const isCompositeId =
					typeof this.offerId === "string" &&
					(/^C\d+$/.test(this.offerId) || /^CO\d+$/.test(this.offerId));
				if (isCompositeId) {
					throw new Error("Oferta compusă nu a putut fi încărcată. Verificați conexiunea sau încercați din nou.");
				}
				offerAndProducts = await fetchOfferWithItemsById(
					this.offerId,
					this
				);
				if (!offerAndProducts) {
					throw new Error("Offer not found");
				}
				this.offer = offerAndProducts.offer;
				if ("productDetailsData" in this) {
					this.productDetailsData = offerAndProducts.offer?.product_details ?? {};
				}
				this.offeredProducts = offerAndProducts.products;
				this.initialOfferProducts = JSON.parse(
					JSON.stringify(offerAndProducts.products)
				);
				this.captureSavedOfferItemsSnapshot(offerAndProducts.products);

				this.recommendedProducts =
					offerAndProducts.recommended_products;
				this.discount = Number(this.offer.discount);
				this.offerPaymentTerms = this.offer.payment_terms;
				this.savedOfferPriceListId =
					this.resolveSavedOfferPriceListId(this.offer);
			}
		}
		this.clientId = this.$route.params.clientId;
		this.client = await getClientInfo(this.clientId, this);
		this.locationId = this.$route.params.locationId;
		this.location = await fetchLocationById(this.locationId, this);

		// IPA: comp/cust come from offer (or composite); overwrite location with offer values. New offer starts with 0.
		if (this.$options.name === "OfferIPA" && this.offer) {
			const o = this.offer;
			if (o.compartmentation != null && o.compartmentation !== undefined) {
				this.location.compartmentation = Number(o.compartmentation);
			}
			if (o.customization != null && o.customization !== undefined) {
				this.location.customization = Number(o.customization);
			}
		}
		if (this.$options.name === "OfferIPA" && !this.offer) {
			// New offer: do not reuse comp/cust from location (they are per-offer, not persisted on location)
			this.location.compartmentation = 0;
			this.location.customization = 0;
		} else {
			this.location.compartmentation = this.location.compartmentation ?? 0;
			this.location.customization = this.location.customization ?? 0;
		}
		// UNITS data is now related to the offer in the back-end and not to the location
		this.location.units = offerAndProducts.units;
		// here we also store the units in the locationUnits variable for saving in the offer as
		// the location.units gets overwritten when location is updated
		this.locationUnits = offerAndProducts.units;
		this.initialLocation = JSON.parse(JSON.stringify(this.location));
		this.loadProductTable = true;

		// Import flow: when opening a new offer with ?importOfferId=<id>,
		// preload only copyable data (codes, quantities, sizing/setup, relevant filters).
		if (!this.offerId && this.$route?.query?.importOfferId) {
			await this.importOfferData(this.$route.query.importOfferId);
		}
	},
	mounted() {
		this.flushPendingImportedDiscountSync();
	},

	computed: {
		/**
		 * Determines whether the Save Offer button should be enabled.
		 * IPA and VMC stay enabled after the first save unless the price list was changed
		 * (or IPA still needs recalculation / has no savable products).
		 * Other offer types require pending product changes.
		 * Applies different rules depending on whether the user is the creator or a technical user.
		 *
		 * @returns {boolean}
		 */
		canSaveOffer() {
			const isIpaOrVmc =
				this.$options.name === "OfferVMC" ||
				this.$options.name === "OfferIPA";
			if (!isIpaOrVmc && !this.isProductsChanged) return false;
			if (this.offerId && this.isChangedOfferPriceList) return false;
			if (this.isPriceListChangedFromSaved()) return false;
			// Disable saving for OfferCeiling if recalculation is needed
			if (this.$options.name === "OfferIPA") {
				if (this.needRecalc) return false;
				if (!this.hasSavableOfferProducts()) return false;
			}
			if (
				this.$options.name === "OfferCeiling" &&
				this.productsNeedRecalculation
			) {
				return false;
			}
			// Case 1: User is the offer creator → allow any changes
			if (!this.isDifferentCreator) return true;

			// Case 2: User is not the creator and not technical → disallow save
			if (!this.isTechnicalCreator) return false;

			// Case 3: User is not the creator but is technical → allow only if only discount changed
			return this.evaluateOnlyDiscountChanged();
		},
	},

	methods: {
		collectDiscountReceivers(root, receiverSet = new Set()) {
			if (!root || receiverSet.has(root)) return receiverSet;
			receiverSet.add(root);
			if (Array.isArray(root.$children) && root.$children.length > 0) {
				root.$children.forEach(child =>
					this.collectDiscountReceivers(child, receiverSet)
				);
			}
			return receiverSet;
		},
		syncImportedDiscountToProductTables(importedDiscount) {
			const receivers = new Set();
			let applied = 0;
			const collect = refValue => {
				if (!refValue) return;
				if (Array.isArray(refValue)) {
					refValue.forEach(collect);
					return;
				}
				receivers.add(refValue);
			};
			collect(this.$refs?.productTableGlobal);
			collect(this.$refs?.productTables);
			collect(this.$refs?.productTable);
			this.collectDiscountReceivers(this, receivers);
			receivers.forEach(component => {
				if (typeof component?.handleOfferDiscountEdit === "function") {
					component.handleOfferDiscountEdit(importedDiscount);
					applied += 1;
				}
			});
			return applied > 0;
		},
		flushPendingImportedDiscountSync() {
			const hasPending = Number.isFinite(Number(this._pendingImportedDiscount));
			if (!hasPending) return;
			const importedDiscount = Number(this._pendingImportedDiscount);
			this.offerInformation.discount = importedDiscount;
			this.discount = importedDiscount;
			const appliedToAnyTable = this.syncImportedDiscountToProductTables(importedDiscount);
			if (appliedToAnyTable || this._importedDiscountSyncAttempts >= 10) {
				this._pendingImportedDiscount = null;
				this._importedDiscountSyncAttempts = 0;
				return;
			}
			this._importedDiscountSyncAttempts += 1;
			setTimeout(() => this.flushPendingImportedDiscountSync(), 100);
		},
		async ensureImportedLocationCreated() {
			const shouldCreateOnSave =
				String(this.$route?.query?.importCreateLocation || "") === "1";
			if (!shouldCreateOnSave) return;
			if (this._importTargetLocationCreated) return;
			if (!this.client?.id || !this.location) return;

			const sourceOfferId = this.$route?.query?.importCompositeId
				? String(this.$route.query.importCompositeId)
				: String(this.$route?.query?.importOfferId || "");
			const explicitDescription =
				typeof this.$route?.query?.importLocationDescription === "string"
					? this.$route.query.importLocationDescription.trim()
					: "";
			const sourceLocation = {
				...(this._importSourceLocationPayload || this.location || {}),
				description:
					explicitDescription ||
					this._importSourceLocationPayload?.description ||
					this.location?.description ||
					"",
			};
			try {
				const createdLocationResult = await createLocationFromImportedOffer(
					this.client.id,
					{ id: sourceOfferId || "import", location: sourceLocation }
				);
				const createdLocationId = createdLocationResult?.id;
				if (!createdLocationId) return;
				this.location.id = createdLocationId;
				this.locationId = createdLocationId;
				if (createdLocationResult?.description) {
					this.location.description = createdLocationResult.description;
				}
				if (this.initialLocation) {
					this.initialLocation = {
						...this.initialLocation,
						id: createdLocationId,
						description:
							createdLocationResult?.description ??
							this.initialLocation.description,
					};
				}
				this._importTargetLocationCreated = true;
			} catch (error) {
				showAlertModal(
					this.$store,
					"Nu am putut crea locația importată. Oferta va fi salvată pe locația curentă.",
					"warning",
					7000,
					error
				);
			}
		},
		async importOfferData(sourceOfferId) {
			try {
				const source = await fetchOfferWithItemsById(sourceOfferId, this);
				if (!source?.offer) return;
				const importCompositeId = this.$route?.query?.importCompositeId;
				const compositeSource = importCompositeId
					? await probeCompositeOffer(String(importCompositeId))
					: null;
				const sourceLocationPayload =
					source.offer?.location && typeof source.offer.location === "object"
						? source.offer.location
						: null;
				const sourceRooms = Array.isArray(sourceLocationPayload?.rooms)
					? sourceLocationPayload.rooms
					: [];
				const sourceUnits = Array.isArray(source.units) ? source.units : [];
				const rawImportedCompartmentation =
					compositeSource?.compartmentation ??
					source.offer.compartmentation ??
					sourceLocationPayload?.compartmentation ??
					this.location?.compartmentation ??
					this.offerInformation?.compartmentation ??
					0;
				const rawImportedCustomization =
					compositeSource?.customization ??
					source.offer.customization ??
					sourceLocationPayload?.customization ??
					this.location?.customization ??
					this.offerInformation?.customization ??
					0;
				const importedCompartmentation = Number.isFinite(
					Number(rawImportedCompartmentation)
				)
					? Number(rawImportedCompartmentation)
					: 0;
				const importedCustomization = Number.isFinite(
					Number(rawImportedCustomization)
				)
					? Number(rawImportedCustomization)
					: 0;
				// Business rule: imported offers start with no discount.
				const importedDiscount = 0;
				const importedIsMixed = false;

				const currentType = this.getOfferType()?.type;
				if (Number(source.offer.type) !== Number(currentType)) {
					showAlertModal(
						this.$store,
						"Oferta selectată nu este de același tip și nu poate fi importată.",
						"warning",
						5000
					);
					return;
				}

				this.offerInformation = {
					...this.offerInformation,
					selectedCustomerType:
						source.offer.selected_customer_type ?? null,
					discount: importedDiscount,
					isDiscountMixed: importedIsMixed,
					compartmentation: importedCompartmentation,
					customization: importedCustomization,
				};
				this.discount = importedDiscount;
				this._pendingImportedDiscount = importedDiscount;
				this._importedDiscountSyncAttempts = 0;
				if (this.$options?.name === "OfferIPA") {
					this.location.compartmentation = importedCompartmentation;
					this.location.customization = importedCustomization;
				}
				if (
					(this.$options?.name === "OfferIPA" ||
						this.$options?.name === "OfferVMC") &&
					sourceRooms.length > 0
				) {
					this.location.rooms = JSON.parse(JSON.stringify(sourceRooms));
				}
				if (
					(this.$options?.name === "OfferIPA" ||
						this.$options?.name === "OfferVMC") &&
					sourceUnits.length > 0
				) {
					this.location.units = JSON.parse(JSON.stringify(sourceUnits));
					this.locationUnits = JSON.parse(JSON.stringify(sourceUnits));
				}
				this._importSourceLocationPayload = sourceLocationPayload
					? JSON.parse(JSON.stringify(sourceLocationPayload))
					: null;

				let sourceProducts = Array.isArray(source.products)
					? source.products
					: [];
				if (compositeSource && Array.isArray(compositeSource.offers)) {
					const compValue = Number(importedCompartmentation);
					const shouldMapUnitIds = compValue === 1 || compValue === 2;
					const unitIds =
						shouldMapUnitIds && typeof this.getUnitIds === "function"
							? this.getUnitIds()
							: [];
					const normalizeLabel = value =>
						String(value || "")
							.toLowerCase()
							.normalize("NFD")
							.replace(/[\u0300-\u036f]/g, "")
							.replace(/\s+/g, " ")
							.trim();
					const unitByLabel = new Map();
					if (
						shouldMapUnitIds &&
						typeof this.formatUnitLabel === "function"
					) {
						unitIds.forEach(uid => {
							unitByLabel.set(
								normalizeLabel(this.formatUnitLabel(uid)),
								uid
							);
						});
					}
					const subdivisions = Array.isArray(compositeSource.subdivisions)
						? compositeSource.subdivisions
						: [];
					sourceProducts = compositeSource.offers.flatMap(
						(suboffer, index) => {
							const subdivision = subdivisions[index] || {};
							const resolvedUnitId = shouldMapUnitIds
								? unitByLabel.get(normalizeLabel(subdivision?.label)) ??
									unitIds[index]
								: undefined;
							const subItemsFromSubdivision = Array.isArray(
								subdivision?.products
							)
								? subdivision.products
								: [];
							const subItemsFromOffer = Array.isArray(suboffer?.items)
								? suboffer.items
								: [];
							const subItems = subItemsFromSubdivision.length
								? subItemsFromSubdivision
								: subItemsFromOffer;
							return subItems.map(item => ({
								...item,
								unitId:
									item?.unitId ??
									item?.unit_id ??
									(resolvedUnitId ?? item?.unitId),
							}));
						}
					);
					if (
						shouldMapUnitIds &&
						this.recommendedProductsByUnit &&
						typeof this.recommendedProductsByUnit === "object"
					) {
						const byUnit = {};
						compositeSource.offers.forEach((suboffer, index) => {
							const subdivision = subdivisions[index] || {};
							const resolvedUnitId =
								unitByLabel.get(normalizeLabel(subdivision?.label)) ??
								unitIds[index];
							if (resolvedUnitId === undefined || resolvedUnitId === null) {
								return;
							}
							byUnit[resolvedUnitId] = Array.isArray(
								suboffer?.recommended_products_codes
							)
								? [...suboffer.recommended_products_codes]
								: [];
						});
						this.recommendedProductsByUnit = byUnit;
					}
				} else if (
					this.recommendedProductsByUnit &&
					typeof this.recommendedProductsByUnit === "object"
				) {
					this.recommendedProductsByUnit = {};
				}
				const mappedProducts = sourceProducts
					.map(product => ({
						...product,
						id: undefined,
						offer_id: undefined,
						offer: undefined,
						// Import should start with a clean discount-edit state.
						// Keeping source `manual_update` can preserve old per-product discounts
						// and incorrectly mark imported normal offers as mixed.
						manual_update: false,
						// Imported offers must not carry source discounts.
						discount: product?.is_discountable_product ? 0 : product.discount,
					}))
					// Keep only effective rows in import payload.
					// Zero-qty leftovers from source offers can incorrectly trigger mixed-discount
					// calculations later even though they do not contribute to totals.
					.filter(product => Number(product?.quantity) > 0);

				this.offeredProducts = mappedProducts;
				this.offerProducts = [...mappedProducts];
				if ("productDetailsData" in this) {
					const importedProductDetails =
						compositeSource?.product_details ?? source.offer?.product_details ?? {};
					this.productDetailsData = JSON.parse(
						JSON.stringify(importedProductDetails)
					);
				}

				// Preserve only relevant "filters" from source.
				this.recommendedProducts =
					compositeSource && Array.isArray(compositeSource.offers)
						? [
								...new Set(
									compositeSource.offers.flatMap(suboffer =>
										Array.isArray(suboffer?.recommended_products_codes)
											? suboffer.recommended_products_codes
											: []
									)
								),
							]
						: Array.isArray(source.recommended_products)
							? [...source.recommended_products]
							: [];
				if (this.$options?.name === "OfferIPA" || this.$options?.name === "OfferVMC") {
					this.initialLocation = JSON.parse(JSON.stringify(this.location));
				}
				// ProductTable initializes new offers with discount 0 unless an existing `offer`
				// object exists. For import flow, force-sync imported offer discount into all
				// mounted product tables so it is visible and persisted correctly.
				this.$nextTick(() => {
					this.flushPendingImportedDiscountSync();
				});

				// Room-based offer import: dimensioning exists only in memory until location PUT runs.
				// Without this flag, offer save can skip verifyLocation and the DB keeps an empty location.
				if (
					(this.$options?.name === "OfferIPA" ||
						this.$options?.name === "OfferVMC") &&
					(sourceRooms.length > 0 || sourceUnits.length > 0)
				) {
					this.location.isLocationChanged = true;
				}

				// Import setup/sizing where applicable.
				if (this.$options?.name === "OfferCeiling") {
					const setupResp = await apiClient.get(
						`/api/get-ceiling-setup/${sourceOfferId}/`
					);
					if (setupResp?.data) {
						if (typeof this.setCeilingSetupFromSource === "function") {
							this.setCeilingSetupFromSource(setupResp.data, {
								markRecalculation: false,
							});
						} else {
							this.ceilingSetup = setupResp.data;
						}
					}
				}
				if (this.$options?.name === "OfferVacuum") {
					const setupResp = await apiClient.get(
						`/api/vacuum-setup/${sourceOfferId}/`
					);
					if (setupResp?.data) this.vacuumSetup = setupResp.data;
				}
				if (this.$options?.name === "OfferPlumbing") {
					const setupResp = await apiClient.get(
						`/api/plumbing-setup/${sourceOfferId}/`
					);
					if (setupResp?.data?.plumbing_items) {
						this.selectedPlumbingItems = setupResp.data.plumbing_items;
					}
				}

				this.isProductsChanged = true;
				showAlertModal(
					this.$store,
					"Oferta sursă a fost importată. Verificați și salvați oferta nouă.",
					"success",
					12000
				);
			} catch (error) {
				console.error("Error importing offer data:", error);
				showAlertModal(
					this.$store,
					"A apărut o eroare la importul ofertei.",
					"danger",
					8000,
					error
				);
			}
		},
		async createNewOffer() {
			const canContinue = await this.handleOutdatedPriceListChoiceBeforeWrite();
			if (!canContinue) return;
			const comp = this.getEffectiveCompartmentation();
			if (
				this.$options.name === "OfferIPA" &&
				(comp === 1 || comp === 2)
			) {
				await this.saveCompositeOffer({
					forceCreate: true,
					skipOutdatedPriceListPrompt: true,
				});
				return;
			}
			await this.createOffer();
		},
		getOfferPriceListTypeForChecks() {
			// Most offer flows use GENERAL; Services uses SERVICES list type.
			if (this.$options?.name === "OfferServices") {
				return this.$store?.state?.PRICE_LIST_TYPES?.SERVICES?.value;
			}
			return this.$store?.state?.PRICE_LIST_TYPES?.GENERAL?.value;
		},
		promptOutdatedPriceListChoice({ oldPriceList, newPriceList }) {
			return new Promise(resolve => {
				emitter.emit("show-outdated-pricelist-dialog", {
					oldPriceList,
					newPriceList,
					onChooseOld: () => resolve("old"),
					onChooseNew: () => resolve("new"),
				});
			});
		},
		async handleOutdatedPriceListChoiceBeforeWrite() {
			// Runs only when editing an existing offer loaded from backend.
			if (!this.offerId || !this.offer) return true;

			const selectedPriceListId = Number(
				this.offerPriceList ??
					this.offerInformation?.price_list_id ??
					this.offer?.price_list
			);
			if (!Number.isFinite(selectedPriceListId) || selectedPriceListId <= 0) {
				return true;
			}

			const priceListType = this.getOfferPriceListTypeForChecks();
			if (!priceListType) return true;

			let priceLists = [];
			try {
				priceLists = await fetchPriceLists(this, priceListType);
			} catch (error) {
				// If latest lists cannot be fetched, do not block save/create.
				return true;
			}
			if (!Array.isArray(priceLists) || priceLists.length === 0) return true;

			const latestPriceList = [...priceLists].sort(
				(a, b) => new Date(b.date_applied) - new Date(a.date_applied)
			)[0];
			if (!latestPriceList?.id) return true;
			if (Number(latestPriceList.id) === selectedPriceListId) return true;

			const selectedPriceList = priceLists.find(
				pl => Number(pl.id) === selectedPriceListId
			);
			if (!selectedPriceList?.date_applied) return true;

			const selectedDate = new Date(selectedPriceList.date_applied);
			const latestDate = new Date(latestPriceList.date_applied);
			if (!(selectedDate < latestDate)) return true;

			const choice = await this.promptOutdatedPriceListChoice({
				oldPriceList: selectedPriceList,
				newPriceList: latestPriceList,
			});
			if (choice === "old") {
				return true;
			}

			const latestPriceListId = Number(latestPriceList.id);
			this.syncSelectedPriceListState(latestPriceListId);
			await this.repriceCurrentOfferProducts(latestPriceListId);
			this.isChangedOfferPriceList = true;
			this.isProductsChanged = true;
			return true;
		},
		async repriceCurrentOfferProducts(priceListId) {
			if (!Number.isFinite(Number(priceListId))) return;
			let response;
			try {
				response = await fetchPriceListProductsById(priceListId, this);
			} catch (error) {
				// If repricing fails, do not block the save path.
				return;
			}

			const items = Array.isArray(response?.items) ? response.items : [];
			if (!items.length) return;

			const priceByCode = new Map(
				items.map(item => [
					item.product_code,
					Number(item.price ?? item.list_price ?? 0),
				])
			);

			const applyPrices = products =>
				(products || []).map(product => {
					const updatedPrice = priceByCode.get(product.product_code);
					if (updatedPrice == null) return product;
					return {
						...product,
						price: updatedPrice,
					};
				});

			if (Array.isArray(this.offeredProducts)) {
				this.offeredProducts = applyPrices(this.offeredProducts);
			} else if (
				this.offeredProducts &&
				typeof this.offeredProducts === "object"
			) {
				this.offeredProducts = Object.fromEntries(
					Object.entries(this.offeredProducts).map(([unitId, products]) => [
						unitId,
						Array.isArray(products) ? applyPrices(products) : products,
					])
				);
			}

			if (Array.isArray(this.offerProducts)) {
				this.offerProducts = applyPrices(this.offerProducts);
			}
		},

		/**
		 * Hydrates a composite offer (with multiple subdivisions) into component state.
		 * Populates offer, client, location, products, and related metadata.
		 *
		 * @param {Object} data - The full composite offer payload from the backend.
		 * @returns {void}
		 */
		_hydrateComposite(data) {
			// Store offer with comp/cust explicit (source for getEffectiveCompartmentation; avoids loss on location overwrite)
			const compVal = data.compartmentation != null && data.compartmentation !== undefined
				? Number(data.compartmentation) : (data.compartmentation ?? null);
			const custVal = data.customization != null && data.customization !== undefined
				? Number(data.customization) : (data.customization ?? null);
			this.offer = { ...data, compartmentation: compVal, customization: custVal };
			this.client = data.client_data || data.client || null;
			// Normalize and assign location data
			if (data.location_data) {
				this.location = data.location_data;
			} else if (typeof data.location === "number") {
				this.location = { id: data.location };
			} else {
				this.location = data.location || {};
			}
			if (!Array.isArray(this.location.rooms)) {
				this.location.rooms = [];
			}
			// Comp/cust on location for UI (until next fetchLocationById)
			if (compVal != null) this.location.compartmentation = compVal;
			if (custVal != null) this.location.customization = custVal;
			if ("productDetailsData" in this) {
				this.productDetailsData = data.product_details ?? {};
			}
			// Extract and link all subdivisions (floors, apartments, etc.)
			const subdivisions = Array.isArray(data.subdivisions)
				? data.subdivisions
				: [];
			this.offer.subdivisions = subdivisions;
			this.location.subdivisions = subdivisions;

			const comp = this.getEffectiveCompartmentation();
			const unitIds =
				(comp === 1 || comp === 2) && typeof this.getUnitIds === "function"
					? this.getUnitIds()
					: [];
			const allProducts = subdivisions.flatMap((s, index) => {
				const unitId = unitIds[index];
				return (s.products || []).map(product => ({
					...product,
					unitId:
						product?.unitId ??
						(unitId !== undefined ? unitId : product?.unitId),
				}));
			});
			this.offeredProducts = allProducts;
			this.initialOfferProducts = JSON.parse(JSON.stringify(allProducts));
			// Aggregate recommended products from each suboffer (composite has no own list)
			const offers = Array.isArray(data.offers) ? data.offers : [];
			const extractPriceListId = entry => {
				if (!entry || typeof entry !== "object") return null;
				const candidateValues = [
					entry.price_list,
					entry.price_list_id,
					entry.priceList,
					entry.priceListId,
					entry.pricelist,
					entry.pricelist_id,
					entry.price_list?.id,
					entry.priceList?.id,
				];
				for (const candidate of candidateValues) {
					const normalized = Number(candidate);
					if (Number.isFinite(normalized) && normalized > 0) {
						return normalized;
					}
				}
				return null;
			};
			const compositePriceListId =
				extractPriceListId(data) ?? extractPriceListId(offers[0]);
			if (Number.isFinite(compositePriceListId)) {
				this.offer.price_list = compositePriceListId;
				this.offerInformation.price_list_id = compositePriceListId;
				this.offerPriceList = compositePriceListId;
				this.savedOfferPriceListId = compositePriceListId;
			}
			const recommendedSet = new Set();
			for (const suboffer of offers) {
				const codes = suboffer?.recommended_products_codes;
				if (Array.isArray(codes)) {
					codes.forEach(code => recommendedSet.add(code));
				}
			}
			this.recommendedProducts = [...recommendedSet];
			// For IPA with compartmentation 1 or 2, restore per-unit recommended products so they show after reload
			if (
				this.$options.name === "OfferIPA" &&
				(comp === 1 || comp === 2) &&
				Array.isArray(offers) &&
				typeof this.getUnitIds === "function"
			) {
				const unitIds = this.getUnitIds();
				this.recommendedProductsByUnit = {};
				offers.forEach((offer, i) => {
					const uid = unitIds[i];
					if (uid !== undefined && uid !== null) {
						this.recommendedProductsByUnit[uid] =
							offer.recommended_products_codes || [];
					}
				});
			}
			this.discount = Number(data.discount ?? 0);
			this.offerPaymentTerms = Array.isArray(data.payment_terms)
				? [...data.payment_terms]
				: (this.offerPaymentTerms ?? []);
			if (!data.payment_terms && this.offerPaymentTerms?.length) {
				this.offer.payment_terms = [...this.offerPaymentTerms];
			}
			this.loadProductTable = true;
		},

		_hydrateNormal(offerAndProducts) {
			if (!offerAndProducts) throw new Error("Offer not found");
			this.offer = offerAndProducts.offer;
			if (this.offer?.offers?.length) {
				this.location = this.location || {};
				this.location.subdivisions = this.offer.offers.map(
					(sub, index) => ({
						id: sub.id,
						label: sub.name || `Subdiviziune ${index + 1}`,
						products: sub.items || [],
					})
				);
				this.loadProductTable = true;
			}
			this.recommendedProducts = [];
			this.specialProducts = [];
			this.offeredProducts = offerAndProducts.products;
			this.initialOfferProducts = JSON.parse(
				JSON.stringify(offerAndProducts.products)
			);
			this.captureSavedOfferItemsSnapshot(offerAndProducts.products);
			this.recommendedProducts = offerAndProducts.recommended_products;
			this.discount = Number(this.offer.discount);
			this.offerPaymentTerms = this.offer.payment_terms;
		},

		/**
		 * Compares current offerProducts with the initial ones to detect additions, deletions or modifications.
		 * @returns {boolean} True if products were added, removed, or changed in quantity.
		 */
		hasModifiedProducts() {
			if (
				!Array.isArray(this.offerProducts) ||
				!Array.isArray(this.initialOfferProducts)
			)
				return false;

			const currentMap = new Map(
				this.offerProducts.map(p => [p.product_code, p.quantity])
			);
			const initialMap = new Map(
				this.initialOfferProducts.map(p => [p.product_code, p.quantity])
			);

			// Check for added or changed products
			for (const [code, qty] of currentMap.entries()) {
				if (!initialMap.has(code)) {
					return true; // Product added
				}
				if (qty !== initialMap.get(code)) {
					return true; // Quantity changed
				}
			}

			// Check for removed products
			for (const code of initialMap.keys()) {
				if (!currentMap.has(code)) {
					return true; // Product removed
				}
			}

			return false;
		},
		/**
		 * Fetches recommended VMC products based on the provided category.
		 * Sends an API request to get additional products filtered by category and price list.
		 * Updates the specialProducts property with the fetched data.
		 * Displays an alert in case of error or failure.
		 *
		 * @async
		 * @function getRecommendedProducts
		 * @param {string} category - The category of products to fetch.
		 */
		async getRecommendedProducts(category) {
			try {
				const response = await apiClient.get(
					`/api/additional-products/${category}/?price_list_id=${
						this.offerPriceList ? this.offerPriceList : ""
					}`
				);
				this.specialProducts = response.data;
			} catch (error) {
				console.error(
					"Error fetching recommended products:",
					error.response ? error.response.data : error.message
				);
				showAlertModal(
					this.$store,
					"A intervenit o eroare la preluarea produselor recomandate.",
					"danger",
					12000,
					error
				);
			}
		},
		/**
		 * Sets the custom sorting order for the generated PDF.
		 *
		 * @function setPDFCustomSorting
		 * @param {string} sorting - The sorting order to apply to the PDF.
		 */
		setPDFCustomSorting(sorting) {
			this.sortingPDF = sorting;
		},
		/**
		 * Updates the custom heading for the PDF.
		 * Marks the offer as changed by setting the isProductsChanged flag to true.
		 *
		 * @function setCustomPDFHeading
		 * @param {string} heading - The custom heading to set for the PDF.
		 */
		setCustomPDFHeading(heading) {
			this.offerInformation.customPDFHeading = heading;
			this.isProductsChanged = true;
		},
		/**
		 * Updates the price list ID for the offer.
		 * For new offers, a price list change marks the offer as changed and enables save.
		 * For saved offers, changing the price list disables save until the original list is restored.
		 *
		 * @function updateOfferPriceList
		 * @param {number} priceListId - The ID of the new price list to set for the offer.
		 */
		async updateOfferPriceList(priceListId) {
			const normalizedPriceListId = Number(priceListId);
			if (
				!Number.isFinite(normalizedPriceListId) ||
				normalizedPriceListId <= 0
			) {
				return;
			}

			this.ensureSavedOfferPriceListBaseline(normalizedPriceListId);

			if (this.offerId) {
				const baselinePriceListId = Number(this.savedOfferPriceListId);
				this.isChangedOfferPriceList =
					Number.isFinite(baselinePriceListId) &&
					baselinePriceListId > 0 &&
					baselinePriceListId !== normalizedPriceListId;
			} else if (
				this.offerPriceList &&
				Number(this.offerPriceList) !== normalizedPriceListId
			) {
				this.isChangedOfferPriceList = true;
				this.isProductsChanged = true;
			}
			this.syncSelectedPriceListState(normalizedPriceListId);
			const syncPromise = this.syncCalculationProductsWithSelectedPriceList(
				normalizedPriceListId
			);
			this._priceListSyncPromise = syncPromise;
			try {
				const priceListProducts = await syncPromise;
				if (
					priceListProducts !== null &&
					this.offerId &&
					this.isChangedOfferPriceList
				) {
					await this.notifySavedOfferPricelistInconsistency(
						priceListProducts
					);
				}
			} finally {
				if (this._priceListSyncPromise === syncPromise) {
					this._priceListSyncPromise = null;
				}
			}
		},
		async awaitPendingPriceListSync() {
			if (!this._priceListSyncPromise) {
				return;
			}
			try {
				await this._priceListSyncPromise;
			} catch (error) {
				// Ignore sync errors here; calculation flow handles missing data downstream.
			}
		},
		async syncCalculationProductsWithSelectedPriceList(priceListId) {
			if (!Number.isFinite(priceListId) || priceListId <= 0) {
				return null;
			}
			const requestedPriceListId = Number(priceListId);

			let response;
			try {
				response = await fetchPriceListProductsById(
					requestedPriceListId,
					this
				);
			} catch (error) {
				return null;
			}

			// Ignore stale responses and keep products synced with current selected price list.
			if (Number(this.offerPriceList) !== requestedPriceListId) {
				return null;
			}

			const fetchedItems = Array.isArray(response?.items)
				? response.items
				: Array.isArray(response)
					? response
					: [];
			if (!fetchedItems.length) {
				return [];
			}

			const nextProducts = cloneDeep(fetchedItems);
			const productsWithZeroQty = this.setZeroQtyForBackendProducts(
				nextProducts
			);
			const hasSelectedProductCategories = Array.isArray(
				this.offerType?.productCategories
			)
				? this.offerType.productCategories.length > 0
				: false;
			const productsForCalculations =
				typeof this.reassignSubCategories === "function" &&
				hasSelectedProductCategories
				? this.reassignSubCategories(productsWithZeroQty)
				: productsWithZeroQty;

			if (Array.isArray(this.products)) {
				this.products = productsForCalculations;
			}
			return fetchedItems;
		},
		patchOfferPdfVisibilityFlags({
			locationVisibleOnPdf,
			areaVisibleOnPdf,
		} = {}) {
			if (!this.offer || typeof this.offer !== "object") {
				return;
			}
			const updates = {};
			if (locationVisibleOnPdf !== undefined) {
				updates.location_visible_on_pdf = Boolean(locationVisibleOnPdf);
			}
			if (areaVisibleOnPdf !== undefined) {
				updates.area_visible_on_pdf = Boolean(areaVisibleOnPdf);
			}
			if (!Object.keys(updates).length) {
				return;
			}
			if (Array.isArray(this.offer.offers)) {
				this.offer = {
					...this.offer,
					...updates,
					offers: this.offer.offers.map(suboffer => ({
						...suboffer,
						...updates,
					})),
				};
			} else {
				this.offer = { ...this.offer, ...updates };
			}
		},
		ensureSavedOfferPriceListBaseline(priceListId) {
			if (!this.offerId || this.savedOfferPriceListId != null) {
				return;
			}
			const fromOffer = this.resolveSavedOfferPriceListId(this.offer);
			const normalized = Number(priceListId);
			const current = Number(this.offerPriceList);
			this.savedOfferPriceListId =
				fromOffer ??
				(Number.isFinite(current) && current > 0 ? current : normalized);
		},
		resolveSavedOfferPriceListId(offer) {
			if (!offer || typeof offer !== "object") return null;
			const candidateValues = [
				offer.price_list,
				offer.price_list_id,
				offer.priceList,
				offer.priceListId,
				offer.price_list?.id,
				offer.priceList?.id,
			];
			for (const candidate of candidateValues) {
				const normalized = Number(candidate);
				if (Number.isFinite(normalized) && normalized > 0) {
					return normalized;
				}
			}
			return null;
		},
		isPriceListChangedFromSaved() {
			if (!this.offerId) return false;
			const savedPriceListId = Number(this.savedOfferPriceListId);
			if (!Number.isFinite(savedPriceListId) || savedPriceListId <= 0) {
				return false;
			}
			const currentPriceListId = Number(
				this.offerPriceList ?? this.offerInformation?.price_list_id
			);
			if (!Number.isFinite(currentPriceListId) || currentPriceListId <= 0) {
				return false;
			}
			return savedPriceListId !== currentPriceListId;
		},
		syncSelectedPriceListState(priceListId) {
			const normalizedPriceListId = Number(priceListId);
			if (
				!Number.isFinite(normalizedPriceListId) ||
				normalizedPriceListId <= 0
			) {
				return;
			}
			this.offerPriceList = normalizedPriceListId;
			this.offerInformation.price_list_id = normalizedPriceListId;
			if (this.offer && typeof this.offer === "object") {
				const locationVisibleOnPdf = Boolean(
					this.offerInformation?.locationVisibleOnPdf ??
						this.offer?.location_visible_on_pdf ??
						this.offer?.offers?.[0]?.location_visible_on_pdf ??
						false
				);
				const areaVisibleOnPdf = Boolean(
					this.offerInformation?.areaVisibleOnPdf ??
						this.offer?.area_visible_on_pdf ??
						this.offer?.offers?.[0]?.area_visible_on_pdf ??
						false
				);
				this.offer = {
					...this.offer,
					price_list: normalizedPriceListId,
					price_list_id: normalizedPriceListId,
					location_visible_on_pdf: locationVisibleOnPdf,
					area_visible_on_pdf: areaVisibleOnPdf,
					offers: Array.isArray(this.offer.offers)
						? this.offer.offers.map(suboffer => ({
							...suboffer,
							price_list: normalizedPriceListId,
							price_list_id: normalizedPriceListId,
							location_visible_on_pdf: locationVisibleOnPdf,
							area_visible_on_pdf: areaVisibleOnPdf,
						}))
						: this.offer.offers,
				};
			}
		},
		/** Compartmentation: location (UI/sync), then offer (on load), then offerInformation, then 0. For new offer we do not use this.offer. */
		getEffectiveCompartmentation() {
			if (!this.offerId) {
				return Number(this.location?.compartmentation ?? this.offerInformation?.compartmentation ?? 0);
			}
			return Number(
				this.location?.compartmentation ??
					this.offer?.compartmentation ??
					this.offerInformation?.compartmentation ??
					0
			);
		},
		/** Customization: location (UI/sync), then offer (on load), then offerInformation, then 0. For new offer we do not use this.offer. */
		getEffectiveCustomization() {
			if (!this.offerId) {
				return Number(this.location?.customization ?? this.offerInformation?.customization ?? 0);
			}
			return Number(
				this.location?.customization ??
					this.offer?.customization ??
					this.offerInformation?.customization ??
					0
			);
		},
		/**
		 * Replaces the measurement unit text in each product with its corresponding numeral value based on a given list of measurement units.
		 * Each product's `measurement_unit` is matched against the provided `measurementUnits` array to find a corresponding unit. If a match is found,
		 * the product's `measurement_unit` is updated to the numeral value of the matching unit.
		 *
		 * @param {Object[]} products - An array of product objects. Each product should have a `measurement_unit` property.
		 * @param {Object[]} measurementUnits - An array of measurement unit objects, where each object has a `text` property (the text representation of the unit)
		 *                                       and a `value` property (the numeral representation of the unit).
		 * @returns {Object[]} The updated array of products with their `measurement_unit` properties replaced by their corresponding numeral values, if matches were found.
		 */
		replaceMeasurementUnits(products, measurementUnits) {
			// Some offer flows may temporarily hold products grouped in an object
			// (e.g. { "no-unit": [...] } or { [unitId]: [...] }). Normalize to array.
			if (!Array.isArray(products)) {
				if (products && typeof products === "object") {
					if (Array.isArray(products["no-unit"])) {
						products = products["no-unit"];
					} else {
						products = Object.values(products).flatMap(v =>
							Array.isArray(v) ? v : []
						);
					}
				} else {
					return [];
				}
			}

			// Loop through each product
			products.forEach(product => {
				// Find the matching measurement unit
				const match = measurementUnits.find(
					unit => unit.text === product.measurement_unit
				);

				// If a match is found, replace the measurement_unit with the numeral value
				if (match) {
					product.measurement_unit = match.value;
				}
			});

			return products;
		},
		/**
		 * Determines the offer type based on the current route name. It searches through an array of `offerTypes` to find
		 * an offer that matches the current route name. The `offerTypes` array is expected to be available within the context
		 * of where this method is defined.
		 *
		 * @returns {Object|undefined} Returns the matching offer object if found; otherwise, returns `undefined`. The offer object is expected
		 *                             to contain at least a `routeName` property that matches the current route name.
		 */
		getOfferType() {
			let routeName = this.$route.name;
			const offer = this.offerTypes.find(
				offer => offer.routeName === routeName
			);
			return offer;
		},
		/**
		 * Sends a POST request to save the ceiling setup configuration for a specific offer.
		 *
		 * This function is used to persist ceiling-specific data (such as total area and number of floors)
		 * related to an offer into the backend. It builds a payload using the provided values and includes
		 * the current offer's ID. If the values for `area` or `floors` are missing or empty strings,
		 * they will be converted to `null` to ensure proper backend validation.
		 *
		 * The API endpoint is expected to return a confirmation message or raise a validation error.
		 *
		 * @async
		 * @function saveCeilingSetup
		 * @param {Object} ceilingSetup - The configuration object for the ceiling setup.
		 * @param {string|number|null} ceilingSetup.area - The total area in square meters. Can be a number, string, or null.
		 * @param {string|number|null} ceilingSetup.floors - The number of floors. Can be a number, string, or null.
		 * @returns {Promise<Object>} Resolves with the API response data on successful save.
		 * @throws {Error} Throws an error if the API request fails due to validation errors or network issues.
		 *
		 * @example
		 * await saveCeilingSetup({ area: 120, floors: 2 });
		 *
		 * @example
		 * await saveCeilingSetup({ area: "", floors: "" }); // Will send null values to the API
		 */
		async saveCeilingSetup(ceilingSetup) {
			const payload = {
				offer: this.offerId,
				area: ceilingSetup.area || null,
				floors: ceilingSetup.floors || null,
			};
			try {
				const response = await apiClient.post(
					"/api/save-ceiling-setup/",
					payload
				);
				return response.data;
			} catch (error) {
				console.error(
					"Eroare la salvarea configurației plafonului:",
					error.response?.data || error.message
				);
				showAlertModal(
					this.$store,
					"A apărut o eroare la salvarea configurării plafonului. Vă rugăm reîncercați.",
					"danger",
					12000,
					error
				);
				throw error;
			}
		},

		/**
		 * Saves the vacuum setup data to the backend.
		 * @param {Object} vacuumSetup - The vacuum setup data to be saved.
		 * @returns {Promise<void>} A promise that resolves when the data is saved successfully.
		 */
		async saveVacuumSetup(vacuumSetup) {
			const transformedData = this.transformVacuumSetup(vacuumSetup);

			try {
				const response = await apiClient.post(
					"/api/save-vacuum-setup/",
					transformedData
				);
				return response.data;
			} catch (error) {
				console.error(
					"Error saving vacuum setup:",
					error.response ? error.response.data : error.message
				);
				showAlertModal(
					this.$store,
					"A intervenit o eroare la salvarea configurării pentru aspirare. Vă rugăm reîncercați.",
					"danger",
					12000,
					error
				);
				throw error;
			}
		},
		/**
		 * Saves the plumbing setup for a given offer.
		 * Sends the plumbing setup data along with the offer ID to the server to be saved.
		 * If an error occurs during the process, it logs the error and displays an alert to the user.
		 *
		 * @param {Array} plumbingSetup - The array of plumbing items to be saved.
		 * @param {string} offerId - The ID of the offer associated with the plumbing setup.
		 * @returns {Object} - The response data from the server if the request is successful.
		 * @throws {Error} - Throws an error if the save request fails.
		 */
		async savePlumbingSetup(plumbingSetup, offerId) {
			try {
				const payload = {
					offer: offerId, // Ensure you're passing the offer ID correctly
					plumbing_items: plumbingSetup, // The plumbing items array
				};
				const response = await apiClient.post(
					"/api/save-plumbing-setup/",
					payload
				);
				return response.data;
			} catch (error) {
				console.error(
					"Error saving plumbing setup:",
					error.response ? error.response.data : error.message
				);
				showAlertModal(
					this.$store,
					"A intervenit o eroare la salvarea configurării pentru sanitare. Vă rugăm reîncercați.",
					"danger",
					12000,
					error
				);
				throw error;
			}
		},
		/**
		 * Initiates the process of saving or creating an offer.
		 * If the current component is 'OfferVMC' and the location is not saved, it triggers 'handleOfferVMCAction'.
		 * Otherwise, it decides whether to save an existing offer or create a new one based on the presence of 'offerId'.
		 */
		async createSaveOffer() {
			const canContinue = await this.handleOutdatedPriceListChoiceBeforeWrite();
			if (!canContinue) return;
			if (this.$options.name === "OfferVMC" && !this.savedLocation) {
				this.isSaveOfferClicked = true;
				const pendingLocationSave = await this.handleOfferVMCAction();
				if (pendingLocationSave) return;
			}
			if (this.$options.name === "OfferIPA" && !this.savedLocation) {
				this.isSaveOfferClicked = true;
				this.handleOfferIPAAction();
				return;
			}

			// IPA composite: comp 1/2 or existing composite offer (CO76) – always save via composite endpoint
			const isCompositeId =
				this.offerId &&
				(/^CO\d+$/.test(String(this.offerId)) || /^C\d+$/.test(String(this.offerId)));
			const comp = this.getEffectiveCompartmentation();
			const useCompositePath =
				this.$options.name === "OfferIPA" &&
				((comp === 1 || comp === 2) || isCompositeId);

			if (this.offerId) {
				if (useCompositePath) {
					this.showRecommendedThermostatWarningIfNeeded?.();
					await this.saveCompositeOffer({
						skipOutdatedPriceListPrompt: true,
					});
					return;
				}
				if (this.$options.name === "OfferIPA")
					this.showRecommendedThermostatWarningIfNeeded?.();
				this.saveOffer();
			} else {
				const compNew = this.getEffectiveCompartmentation();
				if (
					this.$options.name === "OfferIPA" &&
					(compNew === 1 || compNew === 2)
				) {
					this.showRecommendedThermostatWarningIfNeeded?.();
					await this.saveCompositeOffer({
						skipOutdatedPriceListPrompt: true,
					});
					return;
				}
				if (this.$options.name === "OfferIPA")
					this.showRecommendedThermostatWarningIfNeeded?.();
				this.createOffer();
			}
		},
		/**
		 * Checks if the offer creator is different from the current user.
		 * @returns {boolean} True if the offer creator is different, false otherwise.
		 */
		checkDifferentOfferCreator() {
			if (this.offer) {
				const offerAgentId = this.offer.agent;
				const userId = JSON.parse(sessionStorage.getItem("userId"));
				this.isDifferentCreator = offerAgentId !== userId;
				return offerAgentId !== userId;
			}

			return false;
		},
		/**
		 * Evaluates if only the discount was changed and updates the internal flag.
		 * Logs detailed information for debugging.
		 * @returns {boolean} True if only discount was changed.
		 */
		evaluateOnlyDiscountChanged() {
			if (!this.offer) {
				this.onlyDiscountChanged = false;
				return false;
			}

			const originalDiscount = Number(this.offer.discount);
			const currentDiscount = Number(this.discount);

			const notesChanged =
				this.offerInformation.notes &&
				this.offerInformation.notes !== this.offer.notes;
			const hasProductChanges = this.hasModifiedProducts();
			const hasPaymentTermChanges =
				Array.isArray(this.offerPaymentTerms) &&
				this.offerPaymentTerms.length > 0;

			this.onlyDiscountChanged =
				originalDiscount !== currentDiscount &&
				!this.isChangedOfferPriceList &&
				!notesChanged &&
				!hasProductChanges &&
				!hasPaymentTermChanges;

			return this.onlyDiscountChanged;
		},
		/**
		 * Checks if the offer creator is marked as a technical user.
		 * @returns {boolean} True if the offer creator is a technical user.
		 */
		checkIfTechnicalCreator() {
			const offerAgentId = this.offer?.agent;
			const allUsers =
				JSON.parse(sessionStorage.getItem("myUsers")) || [];
			const offerCreator = allUsers.find(
				user => String(user.id) === String(offerAgentId)
			);

			this.isTechnicalCreator = !!(
				offerCreator && offerCreator.is_technicaluser
			);
			return this.isTechnicalCreator;
		},
		/**
		 * Handles the removal of custom header error.
		 */
		handleRemoveCustomHeaderError() {
			this.isCustomPDFHeaderEmpty = false;
		},
		async maybeConfirmPlumbingSaveWithoutRecalculation() {
			if (
				this.$options.name !== "OfferPlumbing" ||
				!this.productsNeedRecalculation
			) {
				return true;
			}
			if (
				typeof this.confirmPlumbingSaveWithoutRecalculation === "function"
			) {
				return await this.confirmPlumbingSaveWithoutRecalculation();
			}
			showAlertModal(
				this.$store,
				"Ați adăugat cantități la fittinguri, dar produsele nu au fost calculate. Vă rugăm apăsați \"Calculează produse\" înainte de a salva oferta!",
				"warning",
				4000
			);
			return false;
		},
		/**
		 * Saves a composite (multi-subdivision) offer to the backend.
		 * Builds suboffers for each subdivision and submits them as a single composite offer payload.
		 *
		 * @async
		 * @returns {Promise<Object|null>} The saved composite offer data, or null if the operation failed.
		 *
		 * @description
		 * - Validates that the client and location exist before proceeding.
		 * - Builds a standardized payload including all subdivisions and their products.
		 * - Automatically maps measurement units to their numeric codes for backend compatibility.
		 * - Displays appropriate alerts for both success and failure cases.
		 */
		async saveCompositeOffer(options = {}) {
			try {
				const {
					forceCreate = false,
					skipOutdatedPriceListPrompt = false,
				} = options;
				if (!skipOutdatedPriceListPrompt) {
					const canContinue =
						await this.handleOutdatedPriceListChoiceBeforeWrite();
					if (!canContinue) return null;
				}
				const location = this.location || {};
				const clientId = location?.client?.id || this.client?.id;
				const agentId =
					this.$store?.state?.user?.id ||
					sessionStorage.getItem("userId");
				const locationId = this.locationId || location?.id;

				// Validate mandatory fields before continuing
				if (!clientId || !locationId) {
					showAlertModal(
						this.$store,
						"Eroare: lipsesc clientul sau locația.",
						"danger",
						6000
					);
					return;
				}
				// Mapping of measurement unit names to backend numeric IDs
				const unitMap = {
					"m²": 1,
					m: 2,
					pungă: 3,
					buc: 4,
					l: 5,
					set: 6,
					cutie: 7,
					rolă: 8,
					km: 9,
					kg: 10,
					ora: 11,
				};
				const toMU = u => (typeof u === "number" ? u : unitMap[u] || 4);

				const subdivisions = this.buildSubdivisionsPayload();

				if (!subdivisions.length) {
					showAlertModal(
						this.$store,
						"Nu există subdiviziuni definite pentru ofertă.",
						"warning",
						4000
					);
					return;
				}

				// Recommended products to store on each suboffer (so they persist when composite is reloaded).
				// For IPA with compartmentation 1 or 2, use per-unit recommended codes; otherwise flat list.
				const comp = this.getEffectiveCompartmentation();
				const isIpaComp12 =
					this.$options.name === "OfferIPA" &&
					(comp === 1 || comp === 2);
				const unitIds =
					isIpaComp12 && typeof this.getUnitIds === "function"
						? this.getUnitIds()
						: [];
				const defaultRecommendedCodes = Array.isArray(
					this.recommendedProducts
				)
					? [...this.recommendedProducts]
					: [];
				const resolvedPriceListId = Number(
					this.offerPriceList ??
						this.offerInformation?.price_list_id ??
						this.offer?.price_list
				);
				if (
					!Number.isFinite(resolvedPriceListId) ||
					Number(resolvedPriceListId) <= 0
				) {
					showAlertModal(
						this.$store,
						"Lista de prețuri lipsește. Selectați o listă de prețuri înainte de salvarea ofertei compuse.",
						"danger",
						7000
					);
					return;
				}
				const locationVisibleOnPdf = Boolean(
					this.offerInformation?.locationVisibleOnPdf
				);
				const areaVisibleOnPdf = Boolean(
					this.offerInformation?.areaVisibleOnPdf
				);
				// Create a separate sub-offer entry for each subdivision
				const suboffers = subdivisions.map((sub, index) => {
					const recommendedCodes =
						isIpaComp12 &&
						unitIds[index] !== undefined &&
						this.recommendedProductsByUnit &&
						Array.isArray(
							this.recommendedProductsByUnit[unitIds[index]]
						)
							? this.recommendedProductsByUnit[unitIds[index]]
							: defaultRecommendedCodes;
					return {
						client: clientId,
						agent: agentId,
						location: locationId,
						name: sub.label || `${location.name} - Subdiviziune`,
						notes: this.offer?.notes || "",
						price_list: resolvedPriceListId,
						price_list_id: resolvedPriceListId,
						location_visible_on_pdf: locationVisibleOnPdf,
						area_visible_on_pdf: areaVisibleOnPdf,
						recommended_products_codes: recommendedCodes,
						items: (sub.products || [])
							.filter(p => Number(p.quantity) > 0)
							.map(p => ({
								product_code: p.product_code,
								product_name: p.product_name || p.name || "",
								quantity: Math.min(
									Number(p.quantity?.toFixed(2)) || 0,
									99999999.99
								),

								price: Number(p.price ?? p.list_price ?? 0),
								discount: Number(p.discount ?? 0),
								measurement_unit: toMU(p.measurement_unit),
								category: p.category,
								assigned_sub_category: p.assigned_sub_category,
								is_deleted: false,
								manual_added: !!p.manual_added,
							})),
						units: [],
					};
				});
				const offerType = this.getOfferType()?.type || 20;
				suboffers.forEach(sub => (sub.type = offerType));

				const terms = (
					this.offerPaymentTerms ||
					this.offer?.payment_terms ||
					[]
				)
					.map(t => (typeof t === "string" ? t.trim() : t))
					.filter(t => t);

				const payload = {
					client: clientId,
					agent: agentId,
					location: locationId,
					name: `${location.name || "Locație"} - ofertă compusă`,
					notes: this.offer?.notes || "",
					type: offerType,
					price_list: resolvedPriceListId,
					price_list_id: resolvedPriceListId,
					suboffers,
					subdivisions,
				};

				if (terms.length) {
					payload.payment_terms = terms;
				}
				// IPA: per-composite compartmentation/customization (offer → location → offerInformation)
				payload.compartmentation = this.getEffectiveCompartmentation();
				payload.customization = this.getEffectiveCustomization();
				// Persist the room-details grouping selection only on offer save (not
				// on "Salvează dimensionare"). Forward the current UI state (including
				// "" for Negrupat); only omit when truly null/undefined (never set).
				if (typeof this.roomsGrouping === "string") {
					payload.rooms_grouping = this.roomsGrouping;
				} else if (typeof this.offer?.rooms_grouping === "string") {
					payload.rooms_grouping = this.offer.rooms_grouping;
				}

				const isExistingComposite =
					!forceCreate &&
					this.offerId &&
					(/^CO\d+$/.test(String(this.offerId)) ||
						/^C\d+$/.test(String(this.offerId)));
				const compositeId = isExistingComposite
					? String(this.offerId).replace(/^CO/, "").replace(/^C/, "")
					: null;

				emitter.emit("trigger-overlay");
				let response;
				if (compositeId) {
					const expectedItemCount = suboffers.reduce(
						(acc, sub) =>
							acc +
							(Array.isArray(sub.items)
								? sub.items.filter(i => Number(i?.quantity) > 0).length
								: 0),
						0
					);
					response = await apiClient.post(
						`/api/update-composite-offer/${compositeId}/`,
						payload
					);
					const returnedOffers = Array.isArray(response?.data?.offers)
						? response.data.offers
						: [];
					const returnedItemCount = returnedOffers.reduce(
						(acc, sub) =>
							acc +
							(Array.isArray(sub?.items)
								? sub.items.filter(i => !i?.is_deleted).length
								: 0),
						0
					);
					if (expectedItemCount !== returnedItemCount) {
						await this.applyCompositeSuboffersFallback(
							suboffers,
							returnedOffers
						);
						const refresh = await apiClient.get(
							`/api/composite-offers/${compositeId}/`
						);
						response = refresh;
					}
				} else {
					response = await apiClient.post(
						"/api/create-composite-offer/",
						payload
					);
				}

				this.offerId = String(response.data.id);
				this.offer = response.data;
				this.syncSelectedPriceListState(this.offerPriceList);

				if (typeof this.saveProductDetailsForOffer === "function") {
					await this.saveProductDetailsForOffer();
				}

				this.$router.push({
					name: offerType.routeName,
					params: {
						offerId: this.offerId,
						clientId: this.clientId,
						locationId: this.locationId,
					},
				});

				if (Array.isArray(response.data.payment_terms)) {
					this.offerPaymentTerms = [...response.data.payment_terms];
				} else if (this.offerPaymentTerms?.length) {
					this.offer.payment_terms = [...this.offerPaymentTerms];
				}
				this.resetFlagsAfterOfferSave?.();
				showAlertModal(
					this.$store,
					isExistingComposite
						? "Oferta compusă a fost actualizată cu succes!"
						: "Oferta compusă a fost salvată cu succes!",
					"success",
					3000
				);
				emitter.emit("trigger-overlay");
				return response.data;
			} catch (err) {
				console.error(
					"Eroare la salvarea ofertei compuse:",
					err?.response?.data || err
				);
				emitter.emit("trigger-overlay");
				showAlertModal(
					this.$store,
					"A apărut o eroare la salvarea ofertei compuse.",
					"danger",
					6000
				);
			}
		},
		async applyCompositeSuboffersFallback(suboffers, targetOffers = []) {
			const existingOffers = Array.isArray(this.offer?.offers)
				? this.offer.offers
				: [];
			const mappedOffers =
				Array.isArray(targetOffers) &&
				targetOffers.length === suboffers.length
					? targetOffers
					: existingOffers;
			if (!mappedOffers.length || mappedOffers.length !== suboffers.length) {
				throw new Error("Cannot map composite suboffers for fallback update.");
			}
			for (let i = 0; i < suboffers.length; i++) {
				const sub = suboffers[i];
				const targetOfferId = mappedOffers[i]?.id;
				if (!targetOfferId) {
					throw new Error("Missing target suboffer id for fallback update.");
				}

				const offerData = {
					discount: Number(this.discount ?? 0),
					notes: this.offer?.notes || "",
					status: 0,
					invoice: 0,
					cap_money: 0,
					price_list_id: this.offerPriceList,
					payment_terms: this.offerPaymentTerms || [],
					recommended_products: sub.recommended_products_codes || [],
					compartmentation: this.getEffectiveCompartmentation(),
					customization: this.getEffectiveCustomization(),
				};

				await apiClient.post(
					`/api/update-simple-offer/${targetOfferId}/`,
					JSON.stringify(offerData),
					{
						headers: { "Content-Type": "application/json" },
					}
				);

				const products = (sub.items || []).map(product => ({
					...product,
					offer_id: targetOfferId,
					offer: targetOfferId,
					quantity: Number(product.quantity) || 0,
				}));
				if (products.length) {
					await updateSelectedProducts(this, products);
				}
			}
		},

		/**
		 * Saves the existing offer details.
		 * Updates the offered products and offer products with measurement units, filters them based on quantity,
		 * and then sends the updated offer data to the server.
		 * On successful update, navigates to a specified route and displays a success message.
		 */
		async saveOffer() {
			try {
				if (
					this.offer &&
					this.offer.type === 4 &&
					!this.offerInformation.customPDFHeading
				) {
					showAlertModal(
						this.$store,
						"Vă rugăm completați câmpul Titlu ofertă PDF!",
						"danger",
						8000
					);
					this.isCustomPDFHeaderEmpty = true;
					return;
				}

				if (this._offerWriteInFlight) return;

				emitter.emit("save-offer-clicked");

				const isDifferent = this.checkDifferentOfferCreator();
				const isTechnical = isDifferent
					? this.checkIfTechnicalCreator()
					: false;

				if (isDifferent && !isTechnical) {
					await this.createOffer();
					return;
				}

				let offer = this.getOfferType();
				this._offerWriteInFlight = true;
				this.isSaveOfferClicked = true;
				if (this.offeredProducts) {
					this.offeredProducts = this.offeredProducts.filter(
						product => Number(product.quantity) > 0
					);
					this.offeredProducts = this.replaceMeasurementUnits(
						this.offeredProducts,
						this.measurementUnits
					);
				}
				if (this.offerProducts) {
					this.offerProducts = this.offerProducts.filter(
						product => Number(product.quantity) > 0
					);
					this.offerProducts = this.replaceMeasurementUnits(
						this.offerProducts,
						this.measurementUnits
					);
				}

				if (this.offeredProducts && !this.offerProducts) {
					this.offerProducts = [...this.offeredProducts];
				}

				// IPA: sync effective compartmentation/customization into offerInformation for save
				if (this.$options.name === "OfferIPA") {
					this.offerInformation.compartmentation =
						this.getEffectiveCompartmentation();
					this.offerInformation.customization =
						this.getEffectiveCustomization();
					// Persist the room-details grouping selection only on offer save
					// (not on "Salvează dimensionare"). Forward the current UI state
					// (including "" for Negrupat). When the user never picked a value
					// (null), keep whatever the offer had before.
					if (typeof this.roomsGrouping === "string") {
						this.offerInformation.rooms_grouping = this.roomsGrouping;
					} else if (typeof this.offer?.rooms_grouping === "string") {
						this.offerInformation.rooms_grouping = this.offer.rooms_grouping;
					} else {
						delete this.offerInformation.rooms_grouping;
					}
				}
				// Keep payload discount aligned with the canonical mixin state.
				// Import flow can temporarily desync `offerInformation.discount` vs `discount`
				// across offer types; normal save/create endpoints read offerInformation.
				this.offerInformation.discount = Number.isFinite(
					Number(this.discount)
				)
					? Number(this.discount)
					: Number(this.offerInformation?.discount || 0);
				const canContinueWithoutRecalculation =
					await this.maybeConfirmPlumbingSaveWithoutRecalculation();
				if (!canContinueWithoutRecalculation) {
					this._offerWriteInFlight = false;
					return;
				}
				await this.ensureVmcLocationPersistedBeforeOfferWrite();
				emitter.emit("show-overlay");
				await updateOffer(
					this,
					offer.type,
					this.offerId,
					this.offerProducts,
					this.offerInformation,
					this.offerPriceList,
					this.offerPaymentTerms,
					this.recommendedProducts,
					this.locationUnits
				)
					.then(async response => {
						this.offeredProducts = response;
						this.syncSelectedPriceListState(this.offerPriceList);
						this.isProductsChanged = false;
						if (
							this.vacuumSetup &&
							this.vacuumSetup.selected_vacuum_unit &&
							this.vacuumSetup.selected_vacuum_unit.product_code &&
							this.vacuumSetup.selected_installation_type &&
							Array.isArray(this.vacuumSetup.selected_connectors) &&
							this.vacuumSetup.selected_connectors.length > 0 &&
							Array.isArray(this.vacuumSetup.floor_areas) &&
							this.vacuumSetup.floor_areas.some(
								f => parseFloat(f.area) > 0
							)
						) {
							await this.saveVacuumSetup(this.vacuumSetup);
						} else if (
							this.selectedPlumbingItems &&
							this.selectedPlumbingItems.length > 0
						) {
							await this.savePlumbingSetup(
								this.selectedPlumbingItems,
								this.offerId
							);
						}
						if (
							this.$options.name === "OfferCeiling" &&
							this.ceilingSetup
						) {
							await this.saveCeilingSetup(this.ceilingSetup);
						}
						if (
							typeof this.saveProductDetailsForOffer ===
							"function"
						) {
							await this.saveProductDetailsForOffer();
						}
						this.$router.push({
							name: offer.routeName,
							params: {
								offerId: this.offerId,
								clientId: this.clientId,
								locationId: this.locationId,
							},
						});

						if (
							this.isSaveOfferClicked &&
							this.location.isLocationChanged &&
							this.savedLocation
						) {
							if (this.isChangedOfferPriceList) {
								showAlertModal(
									this.$store,
									`<b>Dimensionarea</b> a fost salvată. <br> <b>Oferta</b> a fost salvată cu o altă listă de preț fată de cea inițială.`,
									"warning",
									8000
								);
							} else {
								showAlertModal(
									this.$store,
									`<b>Dimensionarea</b> si <b>oferta</b> au fost salvate cu succes.`,
									"success",
									2000
								);
							}
						} else if (this.isSaveOfferClicked) {
							if (this.isChangedOfferPriceList) {
								showAlertModal(
									this.$store,
									"Oferta a fost salvată cu o altă listă de preț fată de cea inițială.",
									"warning",
									8000
								);
							} else {
								showAlertModal(
									this.$store,
									"Oferta a fost salvată cu succes.",
									"success",
									2000
								);
							}
						}
						this.resetFlagsAfterOfferSave();
						emitter.emit("hide-overlay");
					})
					.catch(error => {
						console.error("Error updating offer:", error);
						emitter.emit("hide-overlay");
						showAlertModal(
							this.$store,
							"A apărut o eroare la salvarea ofertei. Vă rugăm să încercați din nou.",
							"danger",
							8000
						);
					})
					.finally(() => {
						this._offerWriteInFlight = false;
					});
			} catch (error) {
				console.error("Error saving offer:", error);
				emitter.emit("hide-overlay");
				this._offerWriteInFlight = false;
				showAlertModal(
					this.$store,
					"A apărut o eroare la salvarea ofertei. Vă rugăm să încercați din nou.",
					"danger",
					8000
				);
			}
		},
		/**
		 * Creates a new offer.
		 * If the current component is 'OfferVMC' and the location is not saved, it triggers 'handleOfferVMCAction'.
		 * Otherwise, it validates the offer products and sends the new offer data to the server.
		 * On successful creation, sets the 'offerId', displays a success message, and navigates to a specified route.
		 */
		async createOffer() {
			let offer = this.getOfferType();
			if (
				offer &&
				offer.type === 4 &&
				!this.offerInformation.customPDFHeading
			) {
				showAlertModal(
					this.$store,
					"Vă rugăm completați câmpul Titlu ofertă PDF!",
					"danger",
					8000
				);
				this.isCustomPDFHeaderEmpty = true;
				return;
			}

			if (this._offerWriteInFlight) return;
			this._offerWriteInFlight = true;

			try {
				this.createNewOfferClicked = true;
				if (this.$options.name === "OfferVMC" && !this.savedLocation) {
					const pendingLocationSave = await this.handleOfferVMCAction();
					if (pendingLocationSave) return;
				}
				if (this.$options.name === "OfferIPA" && !this.savedLocation) {
					await this.handleOfferIPAAction();
					// `handleOfferIPAAction()` may open a dialog (needs user action) OR
					// mark location as already OK by setting `savedLocation=true`.
					if (!this.savedLocation) return;
				}
				await this.ensureVmcLocationPersistedBeforeOfferWrite();
				emitter.emit("save-offer-clicked");

				if (this.offeredProducts.length > 0) {
					this.offerProducts = [...this.offeredProducts];
				}
				if (
					(!this.offerProducts && !this.isProductsChanged) ||
					(this.offeredProducts.length === 0 && !this.isProductsChanged)
				) {
					alert(
						"Nu se poate crea oferta, deoarece nu există produse adăugate (cantitate mai mare ca 0)."
					);
				} else {
				if (this.offeredProducts) {
					this.offeredProducts = this.replaceMeasurementUnits(
						this.offeredProducts,
						this.measurementUnits
					);
				}
				if (this.offerProducts) {
					this.offerProducts = this.replaceMeasurementUnits(
						this.offerProducts,
						this.measurementUnits
					);
				}
				this.offeredProducts = this.offeredProducts.filter(
					product => Number(product.quantity) > 0
				);
				this.offerProducts = this.offerProducts.filter(
					product => Number(product.quantity) > 0
				);
				// Block creation for OfferCeiling if recalculation is needed
				if (
					this.$options.name === "OfferCeiling" &&
					this.productsNeedRecalculation
				) {
					return showAlertModal(
						this.$store,
						"Setup-ul (suprafața/etajele) s-a modificat. Vă rugăm să recalculați produsele înainte de a crea oferta!",
						"warning",
						4000
					);
				}
				// Check if recalculation is needed for OfferPlumbing
				if (
					this.$options.name === "OfferPlumbing" &&
					this.productsNeedRecalculation
				) {
					const canContinueWithoutRecalculation =
						await this.maybeConfirmPlumbingSaveWithoutRecalculation();
					if (!canContinueWithoutRecalculation) return;
				}
				if (this.$options.name === "OfferIPA")
					this.showRecommendedThermostatWarningIfNeeded?.();
				// IPA: sync effective compartmentation/customization into offerInformation for create
				if (this.$options.name === "OfferIPA") {
					this.offerInformation.compartmentation =
						this.getEffectiveCompartmentation();
					this.offerInformation.customization =
						this.getEffectiveCustomization();
					// Persist the room-details grouping selection only on offer save
					// (not on "Salvează dimensionare"). Forward the current UI state
					// (including "" for Negrupat). When the user never picked a value
					// (null), keep whatever the offer had before.
					if (typeof this.roomsGrouping === "string") {
						this.offerInformation.rooms_grouping = this.roomsGrouping;
					} else if (typeof this.offer?.rooms_grouping === "string") {
						this.offerInformation.rooms_grouping = this.offer.rooms_grouping;
					} else {
						delete this.offerInformation.rooms_grouping;
					}
				}
				// Keep payload discount aligned with the canonical mixin state.
				// Import flow can temporarily desync `offerInformation.discount` vs `discount`
				// across offer types; normal save/create endpoints read offerInformation.
				this.offerInformation.discount = Number.isFinite(
					Number(this.discount)
				)
					? Number(this.discount)
					: Number(this.offerInformation?.discount || 0);
				emitter.emit("show-overlay");
				try {
					await createOffer(
						this,
						offer.type,
						this.offerProducts,
						this.offerInformation,
						this.clientId,
						this.locationId,
						sessionStorage.getItem("userId"),
						this.offerPriceList,
						this.offerPaymentTerms,
						this.recommendedProducts,
						this.locationUnits
					).then(async response => {
						this.offerId = response.id.toString();
						this.syncSelectedPriceListState(this.offerPriceList);
						this.$store.commit("setFetchedClientOffers", response);
						this.isProductsChanged = false;

						if (
							this.vacuumSetup &&
							this.vacuumSetup.selected_vacuum_unit &&
							this.vacuumSetup.selected_vacuum_unit.product_code &&
							this.vacuumSetup.selected_installation_type &&
							Array.isArray(this.vacuumSetup.selected_connectors) &&
							this.vacuumSetup.selected_connectors.length > 0 &&
							Array.isArray(this.vacuumSetup.floor_areas) &&
							this.vacuumSetup.floor_areas.some(
								f => parseFloat(f.area) > 0
							)
						) {
							await this.saveVacuumSetup(this.vacuumSetup);
						} else if (
							this.selectedPlumbingItems &&
							this.selectedPlumbingItems.length > 0
						) {
							await this.savePlumbingSetup(
								this.selectedPlumbingItems,
								this.offerId
							);
						} else if (
							this.$options.name === "OfferCeiling" &&
							this.ceilingSetup
						) {
							await this.saveCeilingSetup(this.ceilingSetup);
						}
						if (
							typeof this.saveProductDetailsForOffer ===
							"function"
						) {
							await this.saveProductDetailsForOffer();
						}

						if (this.savedLocation) {
							showAlertModal(
								this.$store,
								`<b>Dimensionarea</b> a fost salvată.</br> <b>Oferta</b> a fost creată cu succes.`,
								"success",
								3000
							);
						} else if (!this.savedLocation) {
							showAlertModal(
								this.$store,
								"Oferta a fost creată cu succes.",
								"success",
								3000
							);
						}
						emitter.emit("hide-overlay");
					});
					fetchOfferWithItemsById(this.offerId, this).then(
						response => {
							this.offeredProducts = response.products;
						}
					);
					this.resetFlagsAfterOfferSave();

					this.$router.push({
						name: offer.routeName,
						params: {
							offerId: this.offerId,
							clientId: this.clientId,
							locationId: this.locationId,
						},
					});
				} catch (error) {
					// Handle error, e.g., display an error message or perform alternative actions
					console.error(error);
					emitter.emit("hide-overlay");
				}
				}
			} finally {
				this._offerWriteInFlight = false;
			}
		},
		/**
		 * Resets various flags related to the offer state and UI indications post saving an offer.
		 * This includes resetting product change flags, save offer click indicators, and offer price list change flags.
		 * Additionally, it handles resetting location change flags and description requirements for rooms
		 * if applicable, ensuring the UI state reflects the latest saved state of an offer.
		 *
		 * The method performs the following actions:
		 * - Resets the flags indicating if products have changed, if the save offer button was clicked,
		 *   and if there has been a change in the offer's price list.
		 * - If the location has been changed (indicated by `isLocationChanged`), this flag is also reset.
		 * - For each room in `roomsDetails` (if present and is an array), it resets the flag indicating
		 *   if a description is needed for each room.
		 * - Directly resets the `isDescriptionNeeded` flag on `roomsDetails` if it exists.
		 *
		 * This method is typically called after an offer is successfully saved or created,
		 * to ensure the application's state is correctly updated to reflect that no pending changes
		 * need to be saved or addressed.
		 *
		 * @see createOffer - Method where `resetFlagsAfterOfferSave` is called after creating an offer.
		 * @see saveOffer - Similar usage as in `createOffer`, for when an offer is saved.
		 */
		resetFlagsAfterOfferSave() {
			this.isProductsChanged = false;
			this.isSaveOfferClicked = false;
			this.createNewOfferClicked = false;
			this.isChangedOfferPriceList = false;
			const currentPriceListId = Number(
				this.offerPriceList ?? this.offerInformation?.price_list_id
			);
			if (Number.isFinite(currentPriceListId) && currentPriceListId > 0) {
				this.savedOfferPriceListId = currentPriceListId;
			}
			if (this.location.isLocationChanged) {
				this.location.isLocationChanged = false;
			}

			// emitting event for setting showOnlyOfferQty on true in ProductTable
			emitter.emit("set-products-with-quantity-true");

			// Safely reset roomsDetails flags according to new version increment requirements
			if (this.$refs.roomsDetails) {
				// Check if roomsDetails has a rooms property and it is an array
				if (Array.isArray(this.$refs.roomsDetails.rooms)) {
					this.$refs.roomsDetails.rooms.forEach(room => {
						// Safely set isDescriptionNeeded to false if room exists
						if (room) {
							room.isDescriptionNeeded = false;
						}
					});
				}
				// Directly set isDescriptionNeeded on roomsDetails if it exists
				if ("isDescriptionNeeded" in this.$refs.roomsDetails) {
					this.$refs.roomsDetails.isDescriptionNeeded = false;
				}
			}
		},
		/**
		 * Handles specific actions for 'OfferVMC'.
		 * If a new version of the room details is required, it sets the description dialog to true.
		 * Otherwise, it saves the location details.
		 */
		async handleOfferVMCAction() {
			await this.ensureImportedLocationCreated();
			const roomsDetails = this.$refs.roomsDetails;
			if (!roomsDetails) return false;

			const needsLocationPersist =
				typeof roomsDetails.needsVmcLocationPersist === "function"
					? roomsDetails.needsVmcLocationPersist()
					: Boolean(this.location?.isLocationChanged);

			if (!needsLocationPersist) {
				if (this.location?.isLocationChanged) {
					this.location.isLocationChanged = false;
				}
				if ("isDescriptionNeeded" in roomsDetails) {
					roomsDetails.isDescriptionNeeded = false;
				}
				if (Array.isArray(roomsDetails.rooms)) {
					roomsDetails.rooms.forEach(room => {
						if (room) {
							room.isDescriptionNeeded = false;
						}
					});
				}
				this.savedLocation = true;
				return false;
			}

			if (roomsDetails.isNewVersionRequired) {
				roomsDetails.setDescriptionDialogTrue();
			} else {
				roomsDetails.saveLocation();
			}
			return true;
		},
		/**
		 * VMC only: persist dimensioning (rooms) via location PUT before offer write.
		 * Runs only when `location.isLocationChanged` (normal edits or `importOfferData` after copy).
		 * No-op for all other offer types; composite IPA uses `saveCompositeOffer` and never hits this.
		 */
		async ensureVmcLocationPersistedBeforeOfferWrite() {
			if (this.$options.name !== "OfferVMC") return;
			if (!this.$refs.roomsDetails || typeof this.verifyLocation !== "function")
				return;
			await this.ensureImportedLocationCreated();
			const needsLocationPersist =
				typeof this.$refs.roomsDetails.needsVmcLocationPersist ===
				"function"
					? this.$refs.roomsDetails.needsVmcLocationPersist()
					: Boolean(this.location?.isLocationChanged);
			if (!needsLocationPersist) {
				if (this.location?.isLocationChanged) {
					this.location.isLocationChanged = false;
				}
				return;
			}
			this.$refs.roomsDetails.deleteEmptyRooms?.();
			this.$refs.roomsDetails.updateLocation(true);
			await this.verifyLocation(
				this.$refs.roomsDetails.change_description || ""
			);
		},
		async handleOfferIPAAction() {
			if (this.$refs.roomsDetails.isNewVersionRequired) {
				this.$refs.roomsDetails.setDescriptionDialogTrue();
				return;
			}

			// Sync IPA room data (circuit_length_ipa, circuit_no, pipe_diameter, etc.) from
			// RoomsDetailsIPA into location.rooms before saving. Locations created from
			// other offer types have null for these fields; without this, we'd either skip
			// saving (and dimensioning PDF has no data) or send stale nulls and overwrite DB.
			if (this.$refs.roomsDetails?.emitLocationUpdate) {
				this.$refs.roomsDetails.emitLocationUpdate(true, false);
			}

			await this.ensureImportedLocationCreated();
			await this.saveLocation();
		},
		/**
		 * Updates the offered and offer products with new products data and marks that the products have changed.
		 * @param {Array} products - The new products data to update.
		 */
		updateOfferProducts(products) {
			this.offeredProducts = products;
			this.offerProducts = products;
			this.isProductsChanged = true;
		},
		/**
		 * Updates the payment terms of the offer.
		 * @param {Object} paymentTerms - The new payment terms data to update.
		 */
		updatePaymentTerms(paymentTerms) {
			this.offerPaymentTerms = paymentTerms;
			this.isProductsChanged = true;
		},
		/**
		 * True when the offer has at least one product line with quantity > 0.
		 * Supports flat arrays and composite maps keyed by unit id.
		 *
		 * @returns {boolean}
		 */
		hasSavableOfferProducts() {
			const raw = this.offeredProducts ?? this.offerProducts;
			if (!raw) return false;
			const list = Array.isArray(raw)
				? raw
				: typeof raw === "object"
					? Object.values(raw).flat()
					: [];
			return list.some(product => Number(product?.quantity) > 0);
		},
		/**
		 * Updates various fields of the offer information.
		 * This can include notes, price list ID, totals and discount values.
		 * @param {Object} data - An object containing the offer data fields to update.
		 */
		updateOfferData(data) {
			// check if data.notes not undefined
			if (data.notes) {
				this.offerInformation.notes = data.notes;
				this.isProductsChanged = true;
			}
			// check if data.price_list_id not undefined
			if (data.price_list_id) {
				this.offerInformation.price_list_id = data.price_list_id;
			}
			// check if data.discount not undefined
			if (data.discount || data.discount === 0) {
				this.offerInformation.discount = data.discount;
				this.discount = data.discount;
			}
			// check if data.totalFinalPrice and data.totalPriceWithoutDiscount exists, if they do assign them
			if (data.totalFinalPrice) {
				this.offerInformation.totalFinalPrice = data.totalFinalPrice;
			}
			if (data.totalPriceWithoutDiscount) {
				this.offerInformation.totalPriceWithoutDiscount =
					data.totalPriceWithoutDiscount;
			}
			if (data.isDiscountMixed) {
				this.offerInformation.isDiscountMixed = data.isDiscountMixed;
			}
			if (data.selectedCustomerType) {
				this.offerInformation.selectedCustomerType =
					data.selectedCustomerType;
				this.isProductsChanged = true;
			}
			if (data.locationVisibleOnPdf !== undefined) {
				this.offerInformation.locationVisibleOnPdf =
					data.locationVisibleOnPdf;
				this.patchOfferPdfVisibilityFlags({
					locationVisibleOnPdf: data.locationVisibleOnPdf,
				});
				if (this.hasSavableOfferProducts()) {
					this.isProductsChanged = true;
				}
			}
			if (data.areaVisibleOnPdf !== undefined) {
				this.offerInformation.areaVisibleOnPdf = data.areaVisibleOnPdf;
				this.patchOfferPdfVisibilityFlags({
					areaVisibleOnPdf: data.areaVisibleOnPdf,
				});
				if (this.hasSavableOfferProducts()) {
					this.isProductsChanged = true;
				}
			}
		},
		/**
		 * Compares two objects for deep equality.
		 * Checks if both objects have the same keys and values, recursively comparing nested objects.
		 *
		 * @function objectsAreEqual
		 * @param {Object} objA - The first object to compare.
		 * @param {Object} objB - The second object to compare.
		 * @returns {boolean} - Returns true if the objects are deeply equal, false otherwise.
		 */
		objectsAreEqual(objA, objB) {
			// If both are primitive types or null or undefined
			if (objA === objB) return true;

			// If one of them is null or undefined
			if (!objA || !objB) return false;

			// If one of them is not an object
			if (typeof objA !== "object" || typeof objB !== "object")
				return false;

			let keysA = Object.keys(objA);
			let keysB = Object.keys(objB);

			if (keysA.length !== keysB.length) return false;

			return keysA.every(key => {
				if (!Object.prototype.hasOwnProperty.call(objB, key))
					return false;
				return this.objectsAreEqual(objA[key], objB[key]);
			});
		},
		/**
		 * Retrieves a product from the list of products based on the given product code.
		 *
		 * @function getUnitProduct
		 * @param {string} unitProductCode - The product code to search for.
		 * @param {Array} products - The list of products to search in.
		 * @returns {Object|undefined} - Returns a copy of the matching product object, or undefined if not found.
		 */
		getUnitProduct(unitProductCode, products) {
			let unitProductIndex = products.findIndex(
				item => item.product_code === unitProductCode
			);

			if (unitProductIndex !== -1) {
				return { ...products[unitProductIndex] };
			}
		},
		/**
		 * Processes the product requirements and matches them to available products.
		 * Iterates through requirements and products to find matching items and updates the quantities.
		 * Adds matched products to the resultedProducts array or recommendedProducts array.
		 *
		 * @function getProducts
		 * @param {Object} requirementsObj - The object containing product requirements.
		 * @param {Array} products - The list of available products.
		 */
		getProducts(requirementsObj, products) {
			let newProductAdded = true;
			while (newProductAdded) {
				newProductAdded = false;

				for (let requirement of requirementsObj.requirements) {
					if (requirement.processed) continue;

					for (let product of products) {
						let isMatch = this.matchRequirementToProduct(
							requirement,
							product
						);

						if (isMatch) {
							if (
								requirement.quantity &&
								!requirement.isRecommendedProduct
							) {
								let productQuantity =
									this.offerType?.type === "IPA"
										? parseFloat(requirement.quantity)
										: Math.round(requirement.quantity);
								requirement.processed = true;
								newProductAdded = true;
								product.quantity = productQuantity;

								if (
									requirement.unitId !== undefined &&
									requirement.unitId !== null
								) {
									product.unitId = requirement.unitId;
								}
								if (requirement.apartment)
									product.apartment = requirement.apartment;
								if (requirement.floor)
									product.floor = requirement.floor;
								if (
									!requirement.product_code &&
									product.product_code
								) {
									requirement.product_code =
										product.product_code;
								}

								this.processProduct(cloneDeep(product));
								this.resultedProducts.push(cloneDeep(product));

								// IMPORTANT:
								// A single requirement should map to a single SKU.
								// If multiple products accidentally share the same specifications,
								// continuing the loop would duplicate quantities (seen on IPA foil).
								break;
							} else if (requirement.isRecommendedProduct) {
								requirement.processed = true;
								let code = JSON.parse(
									JSON.stringify(product.product_code)
								);
								// Per-unit recommended (e.g. different automation per floor) only when compartmentation is 1 or 2.
								const comp =
									this.getEffectiveCompartmentation();
								if (
									this.recommendedProductsByUnit != null &&
									requirement.unitId != null &&
									requirement.unitId !== undefined &&
									(comp === 1 || comp === 2)
								) {
									if (
										!this.recommendedProductsByUnit[
											requirement.unitId
										]
									) {
										this.recommendedProductsByUnit[
											requirement.unitId
										] = [];
									}
									this.recommendedProductsByUnit[
										requirement.unitId
									].push(code);
								} else {
									this.recommendedProducts.push(code);
								}
								break;
							} else {
								requirement.processed = true;
								product.quantity = Math.round(
									requirement.quantity
								);
								this.processProduct(cloneDeep(product));
								this.resultedProducts.push(cloneDeep(product));
								newProductAdded = true;

								break;
							}
						}
					}
				}
			}
		},

		/**
		 * Matches a product to a requirement based on its specifications.
		 * Compares the product's specifications against the requirement's type, subtype, and other criteria.
		 * Returns true if the product meets all the requirement criteria, false otherwise.
		 *
		 * @function matchRequirementToProduct
		 * @param {Object} requirement - The requirement object containing product specifications.
		 * @param {Object} product - The product object with specifications to match against.
		 * @returns {boolean} - Returns true if the product matches the requirement, otherwise false.
		 */
		matchRequirementToProduct(requirement, product) {
			let enableDebugLogs = false; // Set this to false to disable logs
			const requirementType = requirement.type;
			if (enableDebugLogs) {
				console.log(`Product ID ${product.id}: ${product.name}`);
			}

			if (requirementType !== product.specifications.type) {
				if (enableDebugLogs) {
					console.log(
						`Product ID ${product.id}: Type mismatch - requirement type ${requirementType} does not match product type ${product.specifications.type}`
					);
				}
				return false;
			}
			if (
				requirement.subtype &&
				product.specifications.subtype &&
				requirement.subtype !== product.specifications.subtype
			) {
				if (enableDebugLogs) {
					console.log(
						`Product ID ${product.id}: Subtype mismatch - requirement subtype ${requirement.subtype} does not match product subtype ${product.specifications.subtype}`
					);
				}
				return false;
			}

			for (let key in requirement) {
				if (
					key === "processed" ||
					key === "quantity" ||
					key === "isRecommendedProduct" ||
					key === "unitId" ||
					key === "pUnitId" ||
					key === "floor" ||
					key === "apartment"
				)
					continue;

				if (
					!Object.prototype.hasOwnProperty.call(
						product.specifications,
						key
					)
				) {
					if (enableDebugLogs) {
						console.log(
							`Product ID ${product.id}: Key missing - product does not have key ${key}`
						);
					}
					return false;
				}

				if (
					isObject(requirement[key]) &&
					isObject(product.specifications[key])
				) {
					if (
						!this.objectsAreEqual(
							requirement[key],
							product.specifications[key]
						)
					) {
						if (enableDebugLogs) {
							console.log(
								`Product ID ${
									product.id
								}: Object mismatch - requirement ${key} ${JSON.stringify(
									requirement[key]
								)} does not match product ${key} ${JSON.stringify(
									product.specifications[key]
								)}`
							);
						}
						return false;
					}
				} else if (
					Array.isArray(requirement[key]) &&
					Array.isArray(product.specifications[key])
				) {
					if (
						!requirement[key].some(reqItem =>
							product.specifications[key].includes(reqItem)
						)
					) {
						if (enableDebugLogs) {
							console.log(
								`Product ID ${
									product.id
								}: Array mismatch - requirement ${key} ${JSON.stringify(
									requirement[key]
								)} does not match product ${key} ${JSON.stringify(
									product.specifications[key]
								)}`
							);
						}
						return false;
					}
				} else if (Array.isArray(product.specifications[key])) {
					if (
						!product.specifications[key].includes(requirement[key])
					) {
						if (enableDebugLogs) {
							console.log(
								`Product ID ${
									product.id
								}: Array mismatch - requirement ${key} ${
									requirement[key]
								} is not included in product ${key} ${JSON.stringify(
									product.specifications[key]
								)}`
							);
						}
						return false;
					}
				} else if (isObject(product.specifications[key])) {
					if (
						!this.objectsAreEqual(
							requirement[key],
							product.specifications[key]
						)
					) {
						if (enableDebugLogs) {
							console.log(
								`Product ID ${
									product.id
								}: Object mismatch - requirement ${key} ${JSON.stringify(
									requirement[key]
								)} does not match product ${key} ${JSON.stringify(
									product.specifications[key]
								)}`
							);
						}
						return false;
					}
				} else if (requirement[key] !== product.specifications[key]) {
					if (enableDebugLogs) {
						console.log(
							`Product ID ${product.id}: Value mismatch - requirement ${key} ${requirement[key]} does not match product ${key} ${product.specifications[key]}`
						);
					}
					return false;
				}
			}
			return true;
		},

		addRecommendedProducts(existingProducts, products) {
			existingProducts.forEach(existingProduct => {
				if (existingProduct.recommendations) {
					existingProduct.recommendations.forEach(recommendation => {
						let recommendationExists =
							this.requirementsObj.requirements.some(req => {
								if (!req.quantity) {
									return false;
								}
								return this.compareRequirements(
									req,
									recommendation
								);
							});

						if (!recommendationExists) {
							let newRecommendation = JSON.parse(
								JSON.stringify(recommendation)
							);
							newRecommendation.isRecommendedProduct = true;
							// Scope recommendation to same unit as the product (e.g. floor automation)
							newRecommendation.unitId =
								existingProduct.unitId ?? null;
							this.requirementsObj.requirements.push(
								newRecommendation
							);
							this.recommendedProductAdded = true;
						}
					});
				}
			});

			if (this.recommendedProductAdded) {
				this.getProducts(this.requirementsObj, products);
			}
		},
		/**
		 * Reassigns subcategories to products based on their main category and product categories.
		 * Filters category assignments and assigns a default subcategory if no match is found.
		 *
		 * @function reassignSubCategories
		 * @param {Array} products - The array of products to reassign subcategories to.
		 * @returns {Array} - Returns the array of products with reassigned subcategories.
		 */
		reassignSubCategories(products) {
			return products.map(product => {
				// Filter out the category_assignments that don't match the productCategories
				const filteredAssignments = product.category_assignments.filter(
					assignment =>
						this.offerType.productCategories.includes(
							assignment.main_category.name
						)
				);
				product.assigned_sub_category = filteredAssignments[0]
					? filteredAssignments[0]?.sub_category?.name
					: "Alte produse";

				// Return the product with adjusted category_assignments
				return {
					...product,
				};
			});
		},
		/**
		 * Flattens offered products whether stored as an array or compartmentation object.
		 * @param {Array|Object|null|undefined} offeredProducts
		 * @returns {Array}
		 */
		flattenOfferedProducts(offeredProducts) {
			if (!offeredProducts) return [];
			if (Array.isArray(offeredProducts)) return offeredProducts;
			return Object.values(offeredProducts).flat();
		},
		/**
		 * Returns products whose codes are absent from the price list.
		 * @param {Array} productEntries
		 * @param {Array} priceListProducts
		 * @returns {Array<{product_code: string, name: string, quantity: number}>}
		 */
		resolveProductCode(product) {
			const candidates = [product?.product_code, product?.part_no];
			for (const candidate of candidates) {
				if (candidate == null) {
					continue;
				}
				const normalized = String(candidate).trim();
				if (normalized) {
					return normalized;
				}
			}
			return null;
		},
		productsShareCode(productA, productB) {
			const codeA = this.resolveProductCode(productA);
			const codeB = this.resolveProductCode(productB);
			return Boolean(codeA && codeB && codeA === codeB);
		},
		findProductsMissingFromPriceList(productEntries, priceListProducts) {
			const priceListCodes = new Set(
				(priceListProducts || [])
					.map(product => this.resolveProductCode(product))
					.filter(Boolean)
			);
			const seen = new Set();
			const missing = [];
			for (const product of productEntries || []) {
				const code = this.resolveProductCode(product);
				if (!code || seen.has(code) || priceListCodes.has(code)) {
					continue;
				}
				seen.add(code);
				missing.push({
					product_code: code,
					name:
						product.product_name ||
						product.name ||
						code,
					quantity: Number(product?.quantity) || 0,
				});
			}
			return missing;
		},
		/**
		 * Returns calculated products required by the engine but absent from the price list.
		 * @param {Array} calculatedProducts
		 * @param {Array} priceListProducts
		 * @returns {Array<{product_code: string, name: string}>}
		 */
		findCalculatedProductsMissingFromPriceList(
			calculatedProducts,
			priceListProducts
		) {
			const priceListCodes = new Set(
				(priceListProducts || [])
					.map(product => this.resolveProductCode(product))
					.filter(Boolean)
			);
			const seen = new Set();
			const missing = [];
			for (const product of calculatedProducts || []) {
				const code = this.resolveProductCode(product);
				if (!code || seen.has(code) || priceListCodes.has(code)) {
					continue;
				}
				if (!(Number(product.quantity) > 0)) {
					continue;
				}
				seen.add(code);
				missing.push({
					product_code: code,
					name:
						product.product_name ||
						product.name ||
						code,
				});
			}
			return missing;
		},
		formatMissingProductsLabel(product) {
			const code = this.resolveProductCode(product) || product?.product_code;
			if (!code) {
				return "";
			}
			const name = product.product_name || product.name;
			return name && name !== code ? `${code} – ${name}` : code;
		},
		normalizeMissingProductsForAlert(missingProducts) {
			const seen = new Set();
			const normalized = [];
			for (const product of missingProducts || []) {
				const code = this.resolveProductCode(product);
				if (!code || seen.has(code)) {
					continue;
				}
				const label = this.formatMissingProductsLabel({
					...product,
					product_code: code,
				});
				if (!label?.trim()) {
					continue;
				}
				seen.add(code);
				normalized.push({
					product_code: code,
					name: product.product_name || product.name || code,
				});
			}
			return normalized;
		},
		buildCopyableProductsMessage(missingProducts) {
			return this.normalizeMissingProductsForAlert(missingProducts)
				.map(product => this.formatMissingProductsLabel(product))
				.filter(Boolean)
				.join("\n");
		},
		buildProductListErrorDetails(missingProducts) {
			const normalized = this.normalizeMissingProductsForAlert(missingProducts);
			return {
				copyableText: normalized
					.map(product => this.formatMissingProductsLabel(product))
					.filter(Boolean)
					.join("\n"),
				isProductList: true,
				missingProducts: normalized,
			};
		},
		/**
		 * Builds the alert message for products missing from the price list.
		 * @returns {string}
		 */
		buildMissingPricelistAlertMessage() {
			return (
				"Atenție! Unele produse necesare lipsesc din lista de prețuri selectată " +
				"și au fost eliminate din calcul — rezultatul ofertei este incomplet. " +
				"Verificați următoarele produse:"
			);
		},
		/**
		 * Queues a warning when required products are missing from the price list.
		 * @param {Array<{product_code: string, name: string}>} missingProducts
		 */
		queueMissingPricelistProductsAlert(missingProducts) {
			const errorDetails = this.buildProductListErrorDetails(missingProducts);
			if (!errorDetails.copyableText?.trim()) return;
			this.$store.commit("queueAlert", {
				message: this.buildMissingPricelistAlertMessage(),
				type: "warning",
				duration: 15000,
				visible: true,
				errorDetails,
			});
		},
		/**
		 * Shows a warning when required products are missing from the price list
		 * and were excluded from the calculation.
		 * @param {Array<{product_code: string, name: string}>} missingProducts
		 */
		showMissingPricelistProductsAlert(missingProducts) {
			const errorDetails = this.buildProductListErrorDetails(missingProducts);
			if (!errorDetails.copyableText?.trim()) return;
			showAlertModal(
				this.$store,
				this.buildMissingPricelistAlertMessage(),
				"warning",
				15000,
				errorDetails
			);
		},
		/**
		 * Normalizes loaded offer lines (qty > 0) for price-list consistency checks.
		 * @param {Array|Object|null|undefined} products
		 * @returns {Array<{product_code: string, name: string, quantity: number}>}
		 */
		buildSavedOfferItemsFromProducts(products) {
			const seen = new Set();
			const savedItems = [];
			for (const item of this.flattenOfferedProducts(products)) {
				const code = this.resolveProductCode(item);
				if (
					!code ||
					seen.has(code) ||
					!(Number(item?.quantity) || 0) > 0
				) {
					continue;
				}
				seen.add(code);
				savedItems.push({
					product_code: code,
					name:
						item.product_name ||
						item.name ||
						code,
					quantity: Number(item.quantity) || 0,
				});
			}
			return savedItems;
		},
		/**
		 * Captures persisted OfferItems from the loaded offer (qty > 0).
		 * @param {Array|Object} products
		 */
		captureSavedOfferItemsSnapshot(products) {
			this.savedOfferItemsSnapshot =
				this.buildSavedOfferItemsFromProducts(products);
		},
		/**
		 * Captures persisted items for composite offers from each suboffer's OfferItems.
		 * @param {Object} compositeData
		 */
		async captureCompositeSavedOfferItemsSnapshot(compositeData) {
			const offers = Array.isArray(compositeData?.offers)
				? compositeData.offers
				: [];
			if (!offers.length) {
				this.captureSavedOfferItemsSnapshot([]);
				return;
			}

			const persistedItems = (
				await Promise.all(
					offers.map(async suboffer => {
						const subofferId = suboffer?.id;
						if (!subofferId) {
							return [];
						}
						try {
							const response = await fetchOfferWithItemsById(
								subofferId,
								this
							);
							return Array.isArray(response?.products)
								? response.products
								: [];
						} catch (error) {
							return [];
						}
					})
				)
			).flat();

			if (persistedItems.length) {
				this.captureSavedOfferItemsSnapshot(persistedItems);
				return;
			}

			const subdivisions = Array.isArray(compositeData?.subdivisions)
				? compositeData.subdivisions
				: [];
			const subdivisionProducts = subdivisions.flatMap(
				subdivision => subdivision?.products || []
			);
			this.captureSavedOfferItemsSnapshot(subdivisionProducts);
		},
		/**
		 * Returns persisted OfferItems from the saved offer snapshot (qty > 0).
		 * @returns {Array}
		 */
		getPersistedOfferItemsWithQuantity() {
			if (this.savedOfferItemsSnapshot?.length) {
				return this.savedOfferItemsSnapshot;
			}
			if (this.initialOfferProducts?.length) {
				return this.buildSavedOfferItemsFromProducts(
					this.initialOfferProducts
				);
			}
			if (
				!isCompositeOfferId(this.offerId) &&
				this.offeredProducts?.length
			) {
				return this.buildSavedOfferItemsFromProducts(
					this.offeredProducts
				);
			}
			return [];
		},
		/**
		 * Compares persisted offer items against the selected and original price lists.
		 * @param {Array} selectedPriceListProducts
		 * @param {Array} originalPriceListProducts
		 * @returns {{ missingFromSelected: Array, missingFromOriginal: Array }}
		 */
		getSavedOfferPricelistInconsistencies(
			selectedPriceListProducts,
			originalPriceListProducts
		) {
			const savedOfferItems = this.getPersistedOfferItemsWithQuantity();
			return {
				missingFromSelected: selectedPriceListProducts?.length
					? this.findProductsMissingFromPriceList(
							savedOfferItems,
							selectedPriceListProducts
						)
					: [],
				missingFromOriginal: originalPriceListProducts?.length
					? this.findProductsMissingFromPriceList(
							savedOfferItems,
							originalPriceListProducts
						)
					: [],
			};
		},
		hasSavedOfferPricelistProductDetails(inconsistencies) {
			return Boolean(
				inconsistencies?.missingFromSelected?.length ||
					inconsistencies?.missingFromOriginal?.length
			);
		},
		/**
		 * Builds the alert message when the selected price list differs from the saved offer's list.
		 * @param {{ missingFromSelected: Array, missingFromOriginal: Array }} inconsistencies
		 * @returns {string}
		 */
		buildSavedOfferPricelistInconsistencyAlertMessage(
			inconsistencies,
			{ forPriceListChange = false } = {}
		) {
			if (
				forPriceListChange &&
				inconsistencies?.missingFromSelected?.length
			) {
				return (
					"Atenție! Lista de prețuri selectată este diferită de cea cu care a fost salvată oferta. " +
					"Unele produse din oferta salvată nu există în lista de prețuri selectată. " +
					"Verificați următoarele produse:"
				);
			}
			let message =
				"Atenție! Lista de prețuri selectată este diferită de cea cu care a fost salvată oferta. " +
				"Continuarea poate duce la inconsistențe între produsele ofertei și listele de prețuri";
			if (this.hasSavedOfferPricelistProductDetails(inconsistencies)) {
				message += ". Verificați detaliile de mai jos:";
			} else {
				message += ".";
			}
			return message;
		},
		/**
		 * Builds the copyable details for saved-offer price list inconsistencies.
		 * @param {{ missingFromSelected: Array, missingFromOriginal: Array }} inconsistencies
		 * @returns {string}
		 */
		buildSavedOfferPricelistInconsistencyCopyableMessage(inconsistencies) {
			const seen = new Set();
			const products = [];
			for (const list of [
				inconsistencies?.missingFromSelected,
				inconsistencies?.missingFromOriginal,
			]) {
				for (const product of list || []) {
					const code = product?.product_code;
					if (!code || seen.has(code)) {
						continue;
					}
					seen.add(code);
					products.push(product);
				}
			}
			return products
				.map(product => this.formatMissingProductsLabel(product))
				.join("\n");
		},
		/**
		 * Shows a warning when saved offer items are missing from the selected price list.
		 * @param {{ missingFromSelected: Array, missingFromOriginal: Array }} inconsistencies
		 */
		showSavedOfferPricelistInconsistencyAlert(
			inconsistencies,
			{ forPriceListChange = false } = {}
		) {
			const hasCopyableDetails =
				this.hasSavedOfferPricelistProductDetails(inconsistencies);
			showAlertModal(
				this.$store,
				this.buildSavedOfferPricelistInconsistencyAlertMessage(
					inconsistencies,
					{ forPriceListChange }
				),
				"warning",
				15000,
				hasCopyableDetails
					? {
							copyableText:
								this.buildSavedOfferPricelistInconsistencyCopyableMessage(
									inconsistencies
								),
							isProductList: true,
						}
					: ""
			);
		},
		/**
		 * Fetches products for a price list by id.
		 * @param {number|string} priceListId
		 * @returns {Promise<Array>}
		 */
		async fetchPriceListProductsByListId(priceListId) {
			const normalizedPriceListId = Number(priceListId);
			if (
				!Number.isFinite(normalizedPriceListId) ||
				normalizedPriceListId <= 0
			) {
				return [];
			}

			let response;
			try {
				response = await fetchPriceListProductsById(
					normalizedPriceListId,
					this
				);
			} catch (error) {
				return [];
			}

			if (Array.isArray(response?.items)) {
				return response.items;
			}
			return Array.isArray(response) ? response : [];
		},
		async fetchSelectedPriceListProducts() {
			return this.fetchPriceListProductsByListId(
				this.offerPriceList ?? this.offerInformation?.price_list_id
			);
		},
		async fetchSavedOfferPriceListProducts() {
			return this.fetchPriceListProductsByListId(
				this.savedOfferPriceListId
			);
		},
		/**
		 * Alerts when the selected price list differs from the saved offer's list.
		 * Only persisted OfferItems are checked against the newly selected price list.
		 * @param {Array} selectedPriceListProducts
		 */
		async notifySavedOfferPricelistInconsistency(
			selectedPriceListProducts
		) {
			if (selectedPriceListProducts === null) {
				return;
			}
			const offerItems = this.getPersistedOfferItemsWithQuantity();
			if (!offerItems.length) {
				return;
			}
			const missingFromSelected = this.findProductsMissingFromPriceList(
				offerItems,
				selectedPriceListProducts || []
			);
			if (!missingFromSelected.length) {
				return;
			}
			this.showSavedOfferPricelistInconsistencyAlert(
				{
					missingFromSelected,
					missingFromOriginal: [],
				},
				{ forPriceListChange: true }
			);
		},
		/**
		 * Detects offer lines missing from the price list (with quantity 0).
		 * @param {{ offerProducts: Array, priceListProducts: Array, missingProducts?: Array }} options
		 * @returns {Array<{product_code: string, name: string, quantity: number}>}
		 */
		getMissingPricelistProducts({
			offerProducts,
			priceListProducts,
			missingProducts,
		}) {
			if (missingProducts?.length) {
				return missingProducts;
			}
			if (this._missingPricelistProductsFromCalc?.length) {
				return this._missingPricelistProductsFromCalc;
			}
			return this.findProductsMissingFromPriceList(
				offerProducts,
				priceListProducts
			).filter(product => (Number(product.quantity) || 0) === 0);
		},
		/**
		 * Shows success alert and queues a warning if products are missing from the price list.
		 * @param {{ offerProducts?: Array, priceListProducts?: Array, missingProducts?: Array }} options
		 */
		showPostCalculationAlerts({
			offerProducts,
			priceListProducts,
			missingProducts,
		} = {}) {
			const missing = this.getMissingPricelistProducts({
				offerProducts,
				priceListProducts,
				missingProducts,
			});
			this.queueMissingPricelistProductsAlert(missing);
			this._missingPricelistProductsFromCalc = [];
			showAlertModal(
				this.$store,
				"Produsele au fost calculate cu succes!",
				"success",
				3000
			);
		},
		/**
		 * Detects offer lines missing from the price list (with quantity 0) and alerts the user.
		 * @param {{ offerProducts: Array, priceListProducts: Array }} options
		 */
		notifyMissingPricelistProducts({
			offerProducts,
			priceListProducts,
		}) {
			const missing = this.getMissingPricelistProducts({
				offerProducts,
				priceListProducts,
			});
			this.showMissingPricelistProductsAlert(missing);
		},
		/**
		 * Matches products from a primary array with those from a secondary array based on the product code.
		 * If a matching product is found, it updates the primary product's quantity and visibility.
		 * Also assigns a specific subcategory for a product with a particular product code.
		 * Missing calculated products are tracked for alerts only (not added to the table).
		 *
		 * @function matchProducts
		 * @param {Array} primaryArray - The array of primary products to be updated.
		 * @param {Array} secondaryArray - The array of secondary products to find matches in.
		 * @returns {Array} - Returns the updated primary array with matched quantities and show properties.
		 */
		matchProducts(primaryArray, secondaryArray) {
			primaryArray = JSON.parse(JSON.stringify(primaryArray));
			secondaryArray = JSON.parse(JSON.stringify(secondaryArray || []));
			this._missingPricelistProductsFromCalc =
				this.findCalculatedProductsMissingFromPriceList(
					secondaryArray,
					primaryArray
				);
			primaryArray.forEach(primaryProduct => {
				const matchingProduct = secondaryArray.find(secondaryProduct =>
					this.productsShareCode(secondaryProduct, primaryProduct)
				);

				if (matchingProduct) {
					if (
						this.resolveProductCode(primaryProduct) === "913.01.62"
					) {
						primaryProduct.assigned_sub_category =
							"Produse Etapa 2";
					}
					// Setting the show property based on the quantity of the product (if it's 0, then it's true)
					primaryProduct.quantity = matchingProduct.quantity;
					primaryProduct.show = matchingProduct.quantity === 0;
				}
			});

			return primaryArray;
		},
		/**
		 * Fetches a list of priced products from the server and stores it in session storage.
		 * If the session storage already contains the price list, it uses that data instead.
		 * The method handles any errors during the fetching process and logs them.
		 */
		async fetchPriceListProducts(priceListType) {
			// Check if the session storage PRICE_LIST_PRODUCTS is empty, if empty fetch from the server
			let priceListProducts = JSON.parse(
				sessionStorage.getItem("PRICE_LIST_PRODUCTS")
			);
			if (!priceListProducts) {
				console.log("Fetching products from the server");
				try {
					const response = await apiClient.get(
						"/api/price_lists/items/",
						{
							params: {
								type: priceListType,
							},
						}
					);
					if (!response || !response.data) {
						throw new Error("Failed to fetch product data");
					}
					sessionStorage.setItem(
						"PRICE_LIST_PRODUCTS",
						JSON.stringify(response.data)
					);
					return response.data;
				} catch (error) {
					console.error(
						"Error fetching product data:",
						error.response ? error.response.data : error.message
					);
					showAlertModal(
						this.$store,
						"A intervenit o eroare la preluarea produselor din lista de prețuri. Vă rugăm reîmprospătați pagina.",
						"danger",
						12000,
						error
					);
					throw error;
				}
			} else {
				return priceListProducts;
			}
		},
		/**
		 * Sets the quantity of each product in the array to zero.
		 *
		 * @function setZeroQtyForBackendProducts
		 * @param {Array} products - The array of products to update.
		 * @returns {Array} - Returns the updated array of products with quantity set to zero.
		 */
		setZeroQtyForBackendProducts(products) {
			products.forEach(product => {
				product.quantity = 0;
			});
			return products;
		},
		/**
		 * Processes a product's requirements and updates the global requirements object.
		 * Multiplies the product quantity with the requirement quantity, checks if the requirement exists,
		 * and adds or updates it in the requirements list. Logs are available if logging is enabled.
		 *
		 * @function processProduct
		 * @param {Object} product - The product to process, containing requirements and quantity.
		 */
		processProduct(product) {
			// Check if product name contains 'cot' to enable logging
			const enableLogging = false;

			if (enableLogging) {
				console.log("Processing product:", product); // Log the product being processed
			}

			if (product.requirements && Array.isArray(product.requirements)) {
				product.requirements.forEach(req => {
					// multiply the req.quantity with the product.quantity while adding error handling
					let totalQuantity = req.quantity * product.quantity;

					if (enableLogging) {
						console.log("Checking requirement:", req); // Log the current requirement being checked
					}

					const existingRequirementIndex =
						this.requirementsObj.requirements.findIndex(r =>
							this.compareRequirements(r, req)
						);

					if (
						existingRequirementIndex !== -1 &&
						!this.requirementsObj.requirements[
							existingRequirementIndex
						].processed
					) {
						if (enableLogging) {
							console.log(
								`Found existing requirement at index ${existingRequirementIndex}. Updating quantity.`
							); // Log when an existing requirement is found
							// show new quantity
							console.log(
								"Previous quantity:",
								this.requirementsObj.requirements[
									existingRequirementIndex
								].quantity
							);
						}
						// Overwriting the value at the found index with the updated requirement
						this.requirementsObj.requirements[
							existingRequirementIndex
						].quantity += totalQuantity;
					} else {
						if (enableLogging) {
							console.log(
								"No existing requirement found. Adding new requirement:",
								req
							); // Log when no existing requirement is found
						}
						req.quantity =
							req.quantity !== null && req.quantity !== undefined
								? totalQuantity
								: 1;
						req = JSON.parse(JSON.stringify(req));
						this.requirementsObj.requirements.push(req);
					}
				});
			} else {
				if (enableLogging) {
					console.warn("No requirements found for product:", product); // Log a warning if no requirements are present
				}
			}
		},
		/**
		 * Processes conditions for a list of products. If a product contains conditions, it applies them.
		 * Skips already processed products and marks products as processed after applying conditions.
		 * Handles errors for invalid condition functions.
		 *
		 * @function processProductConditions
		 * @param {Array} products - The array of products with conditions to process.
		 */
		processProductConditions(products) {
			for (let i = 0; i < products.length; i++) {
				let product = products[i];
				// Skip processing if the product has already been processed
				if (product.processed) {
					continue;
				}

				if (product.conditions && product.conditions.length > 0) {
					for (let j = 0; j < product.conditions.length; j++) {
						// Check if the condition is a function and not null
						if (typeof product.conditions[j] === "function") {
							try {
								product.conditions[j](this);
							} catch (error) {
								console.error(
									`Error processing condition ${j} for product ${product.id}:`,
									error
								);
							}
						}
					}
				}

				// Mark the product as processed
				product.processed = true;
			}
		},
		/**
		 * Processes the resulted products by updating their quantities or adding them to the finalResultedProducts array.
		 * If a product already exists in finalResultedProducts, its quantity is updated.
		 * The processed products are then removed from the resultedProducts array.
		 *
		 * @function processResultedProducts
		 */
		processResultedProducts() {
			const indicesToRemove = [];
			this.resultedProducts.forEach((product, index) => {
				const existingProductIndex =
					this.finalResultedProducts.findIndex(
						p => p.product_code === product.product_code
					);

				if (existingProductIndex !== -1) {
					// Update quantity in finalResultedProducts
					this.finalResultedProducts[existingProductIndex].quantity +=
						product.quantity !== undefined &&
						product.quantity !== null
							? product.quantity
							: 1;

					// Store the index to remove this product from resultedProducts
					indicesToRemove.push(index);
				} else {
					// Add new product to finalResultedProducts
					this.finalResultedProducts.push({
						...product,
						quantity: product.quantity,
					});
				}
			});

			// Remove the products from resultedProducts
			this.resultedProducts = this.resultedProducts.filter(
				(_, index) => !indicesToRemove.includes(index)
			);
		},
		/**
		 * Compares two requirement objects to check if they are equivalent, excluding the 'quantity' and 'processed' properties.
		 * If both objects have the same keys and values, the function returns true, otherwise false.
		 * Recursively compares nested objects.
		 *
		 * @function compareRequirements
		 * @param {Object} objA - The first requirement object to compare.
		 * @param {Object} objB - The second requirement object to compare.
		 * @returns {boolean} - Returns true if the objects are equivalent, otherwise false.
		 */
		compareRequirements(objA, objB) {
			const keysA = Object.keys(objA).filter(
				key => key !== "quantity" && key !== "processed"
			);
			const keysB = Object.keys(objB).filter(
				key => key !== "quantity" && key !== "processed"
			);

			if (keysA.length !== keysB.length) {
				return false;
			}

			for (let key of keysA) {
				if (
					typeof objA[key] === "object" &&
					typeof objB[key] === "object"
				) {
					if (!this.compareRequirements(objA[key], objB[key])) {
						return false;
					}
				} else if (objA[key] !== objB[key]) {
					return false;
				}
			}
			return true;
		},
		/**
		 * Adds or updates the offeredProducts array with the finalResultedProducts.
		 * Used by all offer types (PDC, VMC, Plumbing, Vacuum, IPA).
		 * - Non-IPA (no compartmentation): merges with existing offeredProducts, respects manual_added.
		 * - IPA with compartmentation 0: flat list, replace all (no unit grouping).
		 * - IPA with compartmentation 1 or 2: groups by unitId (floors/apartments), offeredProducts is an object keyed by unit.
		 * Filters out products with null/undefined quantity and shows success alert.
		 *
		 * @function addOrUpdateOfferedProducts
		 */
		addOrUpdateOfferedProducts() {
			const missingPricelistProducts = this.getMissingPricelistProducts({
				offerProducts: this.flattenOfferedProducts(
					this.offeredProducts
				),
				priceListProducts: this.products,
			});
			this._missingPricelistProductsFromCalc = [];

			const compartmentation = this.getEffectiveCompartmentation();
			// Compartmentation (unit grouping) is an IPA-only concept.
			// Other offer types (VMC, Plumbing, etc.) may share the same location and must keep a flat products array.
			const isIpa = this.$options?.name === "OfferIPA";
			const hasCompartmentation =
				isIpa && (compartmentation === 1 || compartmentation === 2);

			const withQuantity = item =>
				item.quantity !== null && item.quantity !== undefined;
			const priceListCodes = new Set(
				(this.products || [])
					.map(product => this.resolveProductCode(product))
					.filter(Boolean)
			);
			const isInPriceList = product =>
				priceListCodes.has(this.resolveProductCode(product));

			if (hasCompartmentation) {
				const filtered = (this.finalResultedProducts || [])
					.filter(withQuantity)
					.filter(isInPriceList);
				const existing =
					this.offeredProducts &&
					typeof this.offeredProducts === "object" &&
					!Array.isArray(this.offeredProducts)
						? this.offeredProducts
						: {};
				const grouped = {};
				for (const p of filtered) {
					const key =
						p.unitId === 0 || p.unitId ? p.unitId : "no-unit";
					if (!grouped[key]) grouped[key] = [];
					const existingItem = (existing[key] || []).find(
						x => x.product_code === p.product_code
					);
					if (existingItem && existingItem.manual_added) {
						grouped[key].push(existingItem);
					} else {
						grouped[key].push(p);
					}
				}
				for (const key of Object.keys(existing)) {
					if (!grouped[key]) grouped[key] = [];
					for (const item of existing[key] || []) {
						if (
							item.manual_added &&
							!grouped[key].some(
								x => x.product_code === item.product_code
							)
						) {
							grouped[key].push(item);
						}
					}
				}
				this.offeredProducts = grouped;
				this.offerProducts = Object.values(grouped)
					.flat()
					.filter(withQuantity);
			} else if (compartmentation === 0) {
				const filtered = (this.finalResultedProducts || [])
					.filter(withQuantity)
					.filter(isInPriceList);
				const existingList = Array.isArray(this.offeredProducts)
					? this.offeredProducts
					: [];
				const merged = [];
				for (const p of filtered) {
					const existingIndex = existingList.findIndex(
						x => x.product_code === p.product_code
					);
					if (
						existingIndex !== -1 &&
						existingList[existingIndex].manual_added
					) {
						merged.push(existingList[existingIndex]);
					} else {
						merged.push(p);
					}
				}
				for (const item of existingList) {
					if (
						item.manual_added &&
						!merged.some(x => x.product_code === item.product_code)
					) {
						merged.push(item);
					}
				}
				this.offeredProducts = merged.filter(withQuantity);
				this.offerProducts = [...this.offeredProducts];
			} else {
				// Non-IPA: merge with existing, respect manual_added
				if (this.offeredProducts) {
					this.finalResultedProducts.forEach(resultedProduct => {
						if (!isInPriceList(resultedProduct)) {
							return;
						}
						const existingIndex = this.offeredProducts.findIndex(
							offeredProduct =>
								offeredProduct.product_code ===
								resultedProduct.product_code
						);
						if (existingIndex !== -1) {
							if (
								!this.offeredProducts[existingIndex]
									.manual_added
							) {
								this.offeredProducts[existingIndex] =
									resultedProduct;
							}
						} else {
							this.offeredProducts.push(resultedProduct);
						}
					});
				} else {
					this.offeredProducts = (
						this.finalResultedProducts || []
					)
						.filter(withQuantity)
						.filter(isInPriceList);
				}
				this.offeredProducts =
					this.offeredProducts.filter(withQuantity);
				this.offerProducts = [...this.offeredProducts];
			}

			this.isProductsChanged = true;
			setTimeout(() => {
				emitter.emit("trigger-overlay");
				this.queueMissingPricelistProductsAlert(missingPricelistProducts);
				showAlertModal(
					this.$store,
					"Produsele au fost calculate cu succes!",
					"success",
					3000
				);
			}, 50);
		},
	},
	watch: {
		isProductsChanged: function () {
			this.isDifferentCreator = this.checkDifferentOfferCreator();
			if (this.isDifferentCreator) {
				this.isTechnicalCreator = this.checkIfTechnicalCreator();
			}
		},
		discount: function (oldVal, newVal) {
			if (newVal < 0) {
				this.discount = null;
			} else if (newVal > 100) {
				this.discount = 100;
			}
		},
		offer(newVal) {
			if (newVal) {
				this.discount = Number(newVal.discount);
			}
		},
		"location.description": function (newVal) {
			// Offer save flows (e.g. IPA saveLocation()) use `initialLocation` as a
			// base payload. When the user edits location description in
			// OfferInformation, we must keep the snapshot in sync or the PUT will
			// overwrite the edited name with the old one.
			if (this.initialLocation && typeof this.initialLocation === "object") {
				this.initialLocation.description = newVal ?? "";
			}
		},
	},
};
